import {
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import PricingSaveIcon from "@mui/icons-material/Check";
import PricingCancelIcon from "@mui/icons-material/Close";
import PricingEditIcon from "@mui/icons-material/EditOutlined";

import Header from "../../../components/Header";
import BridgeLoanResultTab from "../../../components/common/Loans/BridgeLoanResultTab";
import Disclaimer from "../../../components/common/Disclaimer";

import { RootContext } from "../../../context/RootContext";
import {
  intialDetailedLoanSizingState,
  LoansContext,
} from "../../../context/LoansContext";
import {
  AUDIT_RESOURCE_TYPE,
  extractAuditedFieldPaths,
  hasAnyAuditEntry,
  getPricingAdjustmentAuditFieldPath,
  getLeverageAdjustmentAuditFieldPath,
  DEFAULT_AUDIT_COMMENT,
} from "../../../constants/auditFields";
import { flushPendingAuditEdits } from "../../../utils/auditFlush";

import ShareIcon from "../../../assets/ShareIcon.svg";
import CheckedCircle from "../../../assets/CheckedCircle.svg";
import InfoErrorIcon from "../../../assets/InfoErrorIcon.svg";
import InfoMsgIcon from "../../../assets/InfoMsgIcon.svg";
import AlertTriangle from "../../../assets/AlertTriangle.svg";
import WarnigIconYellow from "../../../assets/WarningIconYellow.svg";
import CloseIcon from "../../../assets/Close-Icon-1.svg";

import {
  convertLoanSizingData,
  createLoanInLos,
  fetchAuditedFields,
  loanDataBasedOnLoanId,
  patchSaveApplication,
  postCalculateEligibility,
  postCreateLoan,
  postUpdateLoanUrl,
  postUpdatePropertyUrl,
  uploadFileToCloud,
  uploadWaiverRequestDoc,
} from "../../../services/network";
import {
  fetchGuidelineEvalResult,
  getTestTabResultData,
  handleCondoEligibilityModalChange,
  renderFieldUI,
  sampleCondoEligibility,
} from "../v1/Common";
import Banners from "../v2/Banners/Banners";

import LoanFieldsUIWrapper from "./LoanFieldsUIWrapper";
import {
  SizingType,
  SizingTypeValue,
  submissionFlow,
} from "../../../types/enums";
import {
  calculateCurrentSelectedInterestRate,
  exportHTML,
  formatEscalationTimestamp,
  getCookie,
  getFormattedDate,
  getPercentageValue,
  getTableValue,
  getValue,
  normalizeResultLoanData,
  normalizeSubmitApplicationLoanData,
  normalizeSubmitApplicationPropertyData,
  rehabTypes,
  sanitizeData,
} from "../../../utils/Common";
import { ObjectType } from "../../../types/tableTypes";

import {
  Button,
  Divider,
  Fade,
  Grid,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import {
  getCondoEligibilityFields,
  MerchantsTabLists as TabLists,
} from "../v1/LoanFields";
import { TabPanelProps } from "../../../types/fieldTypes";
import CustomCard from "../../../components/common/BasicCustomCard";
import LeverageTable from "../../../components/LeverageTable";
import moment from "moment";
import { getLeveragePricingTestsConfig } from "../v1/LeveragePricing";
import BuyDownOptionsWrapper from "./OutputFieldsUI/components/BuyDownOptions/BuyDownOptionsWrapper";
import {
  convertQuickToDetailedLoanData,
  fetchGuidelinesAndBuyDownOptions,
  formatCurrency,
  isWaivedTestStillFailing,
  buildWaiverUpdateRequestData,
} from "../../../utils/v2/common";
import { partitionBySupportedFileType } from "../../../utils/fileValidation";
import RequestWaiverWrapper from "./OutputFieldsUI/components/RequestWaivers/RequestWaiverWrapper";
import GeneralWaiverRequestTab from "../../../components/waivers/GeneralWaiverRequestTab";
import RequestGeneralWaiverFooter from "../../../components/waivers/RequestGeneralWaiverFooter";
import {
  fetchAndSyncWaivers,
  mapWaiverLoanIdToDestination,
  refreshWaiversAfterSave,
} from "../../../components/waivers/utils";
import { LoadingButton } from "@mui/lab";
import CustomModal from "../../../components/common/CustomModal";
import { errorSnackBar, snackbarStyle } from "../v1/style";
import {
  getTermSheetBasedOnVersion,
  getTermSheetVersions,
} from "../../../services/termSheet";
import {
  finalPricingTables,
  encodePricingAdjustmentOverrides,
  decodePricingAdjustmentOverrides,
  encodeLeverageAdjustedMaxOverrides,
  decodeLeverageAdjustedMaxOverrides,
} from "../../../utils/mappings";

import AssignedPartner from "./AssignedPartner";
import UploadDialog from "./Documents/UploadDialog";
import { useTermSheetActions } from "../../../hooks/useTermSheetActions";
import { useDebounce } from "../../../hooks/useDebounce";
import { EscalationTarget, Roles } from "../../../utils/enums";

import { fetchPricingExceptionsForLoan } from "../../../utils/pricingException";

export type ParamType = {
  loanType: string | undefined;
  loanId: string | undefined;
  propType: string | undefined;
};

// Module-level, not nested inside LoansPageV2 — it's a pure function of its
// props (children/value/index only, no closure over component state), and
// nesting it inside the component gave it a brand-new function identity on
// every LoansPageV2 re-render. React treats a changed component identity as
// a different component type, so every tab's whole subtree (including
// RequestWaiverWrapper and its state/effects) was fully unmounted and
// remounted on every re-render of this large, frequently-updating page —
// confirmed via diagnostic logging showing RequestWaiverWrapper's mount
// ref resetting to its initial value every ~1-2s, which only happens on a
// genuine remount.
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      key={`tabpanel-${index}`}
    >
      {value === index && (
        <Box>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default function LoansPageV2() {
  const { t } = useTranslation();
  const params = useParams<ParamType>();
  const theme = useTheme();

  const RootContextData = useContext(RootContext);
  const loanContextData = useContext(LoansContext);

  const { loanType, loanId, propType } = params;
  const {
    decodedTokenData,
    setDynamicLoader,
    dynamicConfig,
    templateLabels,
    DSCROrgPartnerMapping,
    setTermSheetVersions,
    showTermSheet,
    setCurrentPath,
    showKeyDealMetrics,
    originatorAccountMappingData,
    userPermissions,
  } = RootContextData;
  const {
    chipsData,
    loanDetails,
    loansApiResponse,
    expandLeverageAndTests,
    leverageTestsConfig,
    leverageAndPricingData,
    testTabData,
    autoCalculatedLoanResults,
    autoCalculatedPropResults,
    condoEligibilityConfig,
    condoEligibility,
    condoEligibilityFormData,
    failedFinalRate,
    fetchData,
    ratesAsOfDate,
    newDebouncedValue,
    noAssignedPartner,
    assignedPartner,
    expandAvailableProgramsView,
    rulesResponse,
    templateResponseData,
    eligiblePartnerResults,
    partnersResults,
    quickLoanSizingData,
    detailedLoanSizingData,
    // setState functions
    setChipsData,
    setSizingType,
    setLoanDetails,
    setShowSnackBar,
    setExpandLeverageAndTests,
    setLeverageTestsConfig,
    setLeverageAndPricingData,
    setAutoCalculatedLoanResults,
    setAutoCalculatedPropResults,
    setLoansApiResponse,
    setCondoEligibilityConfig,
    setCondoEligibility,
    setCondoEligibilityFormData,
    setExpandAvailableProgramsView,
    setRulesResponse,
    setTemplateResponseData,
    setEligiblePartners,
    setEligiblePartnerResults,
    setPartnersResults,
    setQuickLoanSizingData,
    setDetailedLoanSizingData,
    fieldConfig,
    detailedLoanSizingErrors,
    setDetailedLoanSizingErrors,
    setBuyDownOptions,
    buyDownOptions,
    currentSelectedInterestRate,
    setCurrentSelectedInterestRate,
    latestTermSheetVersion,
    selectedSizingType,
    showSnackBar,
    setPriceExceptionStatus,
    setFetchData,
    pendingAuditEdits,
    setPendingAuditEdits,
    setAuditedFieldPaths,
    setFieldAuditHistoryCache,
    isFirstEverAudit,
    setIsFirstEverAudit,
    waiverDecisionVersion,
    setWaiverDecisionVersion,
    waiverRequestData,
    setWaiverRequestData,
    setNewDebouncedValue,
    pricingAdjustmentOverrides,
    setPricingAdjustmentOverrides,
    leverageAdjustedMaxOverrides,
    setLeverageAdjustedMaxOverrides,
    setAssignedPartner
  } = loanContextData || {};

  const isUnderWriter = useMemo(
    () => userPermissions?.includes(Roles.underwriter),
    [userPermissions],
  );
  const isLoanProcessor = useMemo(
    () => userPermissions?.includes(Roles.loanProcessor),
    [userPermissions],
  );
  const isCreditCommittee = useMemo(
    () => userPermissions?.includes(Roles.creditCommittee),
    [userPermissions],
  );
  const canUpdatePricingAdjustment = useMemo(
    () => userPermissions?.includes(Roles.updatePricingAdjustment),
    [userPermissions],
  );

  const latestEscalation = useMemo(() => {
    const escalations = loanDetails?.escalations;
    if (!Array.isArray(escalations) || !escalations.length) return null;
    const lastEntry = escalations[escalations.length - 1];
    // Once the most recent escalation has been approved/rejected, that
    // resolution (not an escalation itself) is the last entry - stop
    // showing the banner rather than falling back to an older escalation.
    return lastEntry?.escalationComment ? lastEntry : null;
  }, [loanDetails?.escalations]);

  const isLatestEscalationByCurrentUser = useMemo(() => {
    const currentUser =
      decodedTokenData?.name ||
      decodedTokenData?.email ||
      decodedTokenData?.user_id ||
      "Unknown User";
    return Boolean(
      latestEscalation?.createdBy && latestEscalation.createdBy === currentUser,
    );
  }, [decodedTokenData, latestEscalation]);

  const ESCALATION_TARGET_LABEL_KEYS: Record<string, string> = {
    [EscalationTarget.processor]: "processorLabel",
    [EscalationTarget.underwriter]: "underwriterLabel",
    [EscalationTarget.creditCommittee]: "creditCommitteeLabel",
  };

  const escalationBannerTitle = useMemo(() => {
    if (!latestEscalation) return "";
    if (isLatestEscalationByCurrentUser) return t("escalatedByYouBanner");

    const targets: string[] = Array.isArray(latestEscalation?.escalatedTo)
      ? latestEscalation.escalatedTo
      : [];
    const viewerIsTarget =
      (isLoanProcessor && targets.includes(EscalationTarget.processor)) ||
      (isUnderWriter && targets.includes(EscalationTarget.underwriter)) ||
      (isCreditCommittee && targets.includes(EscalationTarget.creditCommittee));
    if (viewerIsTarget) return t("escalatedToYouBanner");

    const targetLabels = targets
      .map((target) => ESCALATION_TARGET_LABEL_KEYS[target])
      .filter(Boolean)
      .map((labelKey) => t(labelKey));
    if (!targetLabels.length) return t("escalatedToYouBanner");

    return t("escalatedToTargetBanner", { targets: targetLabels.join("/") });
  }, [
    latestEscalation,
    isLatestEscalationByCurrentUser,
    isLoanProcessor,
    isUnderWriter,
    isCreditCommittee,
    t,
  ]);
  const canRequestGeneralWaiver =
    !!dynamicConfig?.showRequestWaiver &&
    !!userPermissions?.includes(Roles.loanProcessor);

  const [applicationName, setApplicationName] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLeveragePricingOverrideStale, setIsLeveragePricingOverrideStale] =
    useState<boolean>(false);
  // Edit-mode state for the Pricing card lives here (not inside
  // LeverageTable) so the edit/save/cancel icons can render in the card's
  // header row, on the same line as the "Pricing" title — see
  // BasicCustomCard's headingActions. LeverageTable itself just renders
  // inputs bound to pricingDraftValues when isEditingPricingCard is true.
  const [isEditingPricingCard, setIsEditingPricingCard] = useState(false);
  const [pricingDraftValues, setPricingDraftValues] = useState<
    Record<string, string>
  >({});
  const [pricingOriginalValues, setPricingOriginalValues] = useState<
    Record<string, string>
  >({});
  // Flips true the moment a pricing row edit is saved, so the effect below
  // (watching this flag) fires handleSaveApplication on the NEXT render —
  // by then pricingAdjustmentOverrides/leverageAndPricingData/
  // pendingAuditEdits have actually committed, so handleSaveApplication's
  // closure reads the edited values instead of the pre-edit ones a
  // same-tick call would've captured.
  const [pendingPricingApplicationSave, setPendingPricingApplicationSave] =
    useState(false);
  const [isTestTabLoading, setIsTestTabLoading] = useState<boolean>(true);
  const [openSaveApplicationModal, setOpenSaveApplicationModal] =
    useState<boolean>(false);
  const [evaluationID, setEvaluationID] = useState<number>();
  const [openWarningBeforeSubmit, setOpenWarningBeforeSubmit] =
    useState<boolean>(false);
  const [applicationError, setApplicationError] = useState<string>("");
  const [showLoanDetails, setShowLoanDetails] = useState<boolean>(true);
  const [showBanners, setShowBanners] = useState<boolean>(true);
  const [showBridgePricingBanner, setShowBridgePricingBanner] =
    useState<boolean>(false);
  const [tcLoanId, setTcLoanId] = useState<string>("");
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [isGeneralWaiverFormOpen, setIsGeneralWaiverFormOpen] =
    useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<ObjectType>({});
  const [snackBarMsg, setSnackBarMsg] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);
  const [isPricingStale, setIsPricingStale] = useState<boolean>(false);
  const [showWarningForWaiversSubmit, setShowWarningForWaiversSubmit] =
    useState<boolean>(false);
  const [uploading, setUploading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [documentList, setDocumentList] = useState<any[]>([]);
  const [isEditingLeveragesCard, setIsEditingLeveragesCard] = useState(false);
  const [leveragesDraftValues, setLeveragesDraftValues] = useState<Record<string, string>>({});
  const [leveragesOriginalValues, setLeveragesOriginalValues] = useState<Record<string, string>>({});
  const [pendingLeveragesApplicationSave, setPendingLeveragesApplicationSave] = useState(false);

  const [files, setFiles] = useState<
    { file: File; progress: number; error?: any }[]
  >([]);
  // Backup of the full file list for the current upload session.
  // Prevents other setFiles(direct) calls from silently dropping files during a retry.
  const uploadSessionFilesRef = useRef<
    { file: File; progress: number; error?: any }[]
  >([]);

  const { getTermSheetDataBasedOnAction } = useTermSheetActions({
    evaluationID,
    interactive: false,
  });

  const assignedOrTakeOutPartnerId =
    assignedPartner ?? (loanDetails?.takeOutPartner || "");

  const orgFundingType =
    loanDetails?.fundingType || getCookie("orgFundingType");

  const realmHeaders = {
    "kc-realm": dynamicConfig?.ViteAppKcRealm,
  };

  const disclaimer = useMemo(() => {
    const showDisclaimer = dynamicConfig?.disclaimer !== "";
    if (!showDisclaimer) return "";
    return (
      <>
        {dynamicConfig?.disclaimer ?? (
          <span>
            {t("loanDisclaimerText")}{" "}
            <a href={dynamicConfig?.ViteAppTcRedirect} target="_blank">
              {t("toorakConnect")}
            </a>
          </span>
        )}
        {dynamicConfig?.rateSheetLink && (
          <IconButton
            onClick={() => window.open(dynamicConfig?.rateSheetLink, "_blank")}
            data-testid="rate-sheet-icon"
          >
            <img src={ShareIcon} alt="share-icon" />
          </IconButton>
        )}
      </>
    );
  }, [dynamicConfig]);

  // Validates a single field config against a property for validateForm.
// Extracted to keep nested-function depth within validateForm in check.
function validatePropertyFieldConfig(params: {
  config: any;
  property: any;
  key: string;
  propErrorsForIndex: any;
  unitErrors: any[];
}): { isError: boolean; propErrorsForIndex: any } {
  const { config, property, key, unitErrors } = params;
  let propErrorsForIndex = params.propErrorsForIndex;
  let isError = false;

  if (
    config?.required &&
    config?.isVisible(property) &&
    !property[key]?.[config.id] &&
    key !== "unitInformation"
  ) {
    isError = true;
    propErrorsForIndex = {
      ...propErrorsForIndex,
      [key]: {
        ...propErrorsForIndex?.[key],
        [config.id]: "thisFieldIsRequired",
      },
    };
  } else if (
    config?.required &&
    config?.isVisible(property) &&
    key === "unitInformation"
  ) {
    property[key]?.forEach((unit: any, unitIndex: number) => {
      if (!unit[config.id]) {
        unitErrors[unitIndex] = {
          ...unitErrors[unitIndex],
          [config.id]: "thisFieldIsRequired",
        };
      }
    });
  }

  return { isError, propErrorsForIndex };
}

  // Validates every field config for one property's one section (key) and
  // updates propErrors[index] (mutated in place) with the resulting per-field
  // errors. Extracted from validateForm's nested property/key/field loops to
  // keep its nested-function depth in check.
  function validatePropertySectionFields(
    key: string,
    property: any,
    index: number,
    propErrors: any[],
    tempFormErrors: ObjectType,
    fieldConfig: any,
  ): boolean {
    let isError = false;
    // const unitErrors = [
    //   ...((tempFormErrors?.propertyDetails ?? [])?.[index]?.[
    //     "unitInformation"
    //   ] || []),
    // ];
    const unitInfo = tempFormErrors?.propertyDetails?.[index]?.unitInformation;
    const unitErrors = Array.isArray(unitInfo)
      ? [...unitInfo]
      : unitInfo
        ? [unitInfo]
        : [];
    const currentConfig = fieldConfig?.propertyDetails?.[key];
    currentConfig?.fields?.forEach((config: any) => {
      const fieldResult = validatePropertyFieldConfig({
        config,
        property,
        key,
        propErrorsForIndex: propErrors[index],
        unitErrors,
      });
      if (fieldResult.isError) {
        isError = true;
      }
      propErrors[index] = fieldResult.propErrorsForIndex;
    });
    propErrors[index] = {
      ...propErrors[index],
      unitInformation: unitErrors,
    };
    return isError;
  }

  // Computes the updated leverages tableBody with a new "Adjusted Max" value
  // for the given column. Extracted from handleSaveLeveragesAdjustmentRow to
  // keep its nested-function depth in check.
  function computeUpdatedLeveragesTableBody(
    prev: any,
    leverageTestsConfig: any,
    columnKey: string,
    newValue: string,
  ): any {
    if (!Array.isArray(prev?.leverages?.tableBody)) return prev;
    const headerCols: any[] =
      leverageTestsConfig?.find((ele: ObjectType) => ele?.label === "leverages")
        ?.tableHeader ?? [];
    const columnIdx = headerCols.findIndex(
      (col: ObjectType) => col?.label === columnKey,
    );
    if (columnIdx === -1) return prev;
    return {
      ...prev,
      leverages: {
        ...prev.leverages,
        tableBody: prev.leverages.tableBody.map((row: any) =>
          row?.id === "adjustedMax"
            ? {
                ...row,
                data: row.data.map((value: any, idx: number) =>
                  idx === columnIdx ? newValue : value,
                ),
              }
            : row,
        ),
      },
    };
  }

  async function uploadDocument(newFiles: any) {
    if (!newFiles?.length || uploading) return;
    // accept on the file input only filters the native picker dialog —
    // drag-and-drop bypasses it entirely, so unsupported files are also
    // rejected here before anything gets uploaded.
    const { supported, unsupported } = partitionBySupportedFileType(
      Array.from(newFiles) as File[],
    );
    if (unsupported.length) {
      setShowSnackBar(true);
      setSnackBarMsg("unsupportedFileType");
    }
    if (!supported.length) return;
    newFiles = supported;
    setUploading(true);
    setIsOpen(true);
    const initialEntries = newFiles.map((fileObj: any) => ({
      file: fileObj,
      progress: 20,
      error: false,
    }));
    uploadSessionFilesRef.current = initialEntries;
    setFiles(initialEntries);
    try {
      const filesArray = Array.from(newFiles || []);
      const documents: {
        id: string;
        documentName: string;
        documentPath: string;
        date: string;
        isDeleted: boolean;
        deletedOn: Date | null;
        deletedBy: string | null;
      }[] = [...(documentList ?? loanDetails?.documents ?? [])];

      await Promise.all(
        filesArray.map(async (file: any) => {
          const filename = file.name;
          const filePath = `sizerloan/${loanId}/documents/${filename}`;
          const urlData = await uploadWaiverRequestDoc(
            dynamicConfig?.fileUploadUrl ?? "",
            {
              path: filePath,
              originatorId: null,
              originatorLoanId: null,
              internal: false,
            },
          );
          await uploadFileToCloud(file, urlData?.data);
          let strippedFilePath = `${urlData?.data?.split("sizerloan")?.[0]}`;
          strippedFilePath = `${strippedFilePath}${filePath}`;
          documents.push({
            id: Date.now().toString(),
            documentName: filename,
            documentPath: strippedFilePath,
            date: new Date().toISOString(),
            isDeleted: false,
            deletedOn: null,
            deletedBy: null,
          });
        }),
      );
      setFiles(
        newFiles.map((fileObj: any) => ({
          file: fileObj,
          progress: 100,
          error: false,
        })),
      );

      const documentsToBeSaved = [
        ...(loanDetails?.documents ?? []),
        ...documents,
      ];
      const uniqueDocuments = [
        ...new Map(documentsToBeSaved.map((item) => [item.id, item])).values(),
      ];
      await patchSaveApplication(loanId, { documents: uniqueDocuments });
      setLoanDetails((prev: any) => ({ ...prev, documents: uniqueDocuments }));
      setDocumentList(uniqueDocuments);
    } catch (err) {
      console.error("Error while uploading document: ", err);
      setFiles((prev: any[]) =>
        prev.map((f: any) =>
          f.error ? f : { ...f, error: "Upload failed. Please try again." },
        ),
      );
    } finally {
      setUploading(false);
    }
  }

  async function retryDocument(fileIndex: number) {
    if (uploading) return;
    const fileToRetry: any = files[fileIndex]?.file;
    if (!fileToRetry) return;
    setUploading(true);
    setIsOpen(true);

    // Reset the targeted file; restore full session list if prev was somehow truncated
    setFiles((prev: any[]) => {
      const base =
        prev.length >= uploadSessionFilesRef.current.length
          ? prev
          : uploadSessionFilesRef.current.map((orig: any, i: number) => {
              const live =
                prev.find((p: any) => p.file === orig.file) ?? prev[i] ?? orig;
              return {
                ...orig,
                progress: live.progress ?? orig.progress,
                error: live.error ?? orig.error,
              };
            });
      return base.map((f: any, i: number) =>
        i === fileIndex ? { ...f, progress: 20, error: false } : f,
      );
    });

    try {
      const filename = fileToRetry.name;
      const filePath = `sizerloan/${loanId}/documents/${filename}`;
      const urlData = await uploadWaiverRequestDoc(
        dynamicConfig?.fileUploadUrl ?? "",
        {
          path: filePath,
          originatorId: null,
          originatorLoanId: null,
          internal: false,
        },
      );
      await uploadFileToCloud(fileToRetry, urlData?.data);
      let strippedFilePath = `${urlData?.data?.split("sizerloan")?.[0]}`;
      strippedFilePath = `${strippedFilePath}${filePath}`;

      const documentsToBeSaved = [
        ...(documentList ?? loanDetails?.documents ?? []),
        {
          id: Date.now().toString(),
          documentName: filename,
          documentPath: strippedFilePath,
          date: new Date().toISOString(),
          isDeleted: false,
          deletedOn: null,
          deletedBy: null,
        },
      ];
      const uniqueDocuments = [
        ...new Map(documentsToBeSaved.map((item) => [item.id, item])).values(),
      ];
      await patchSaveApplication(loanId, { documents: uniqueDocuments });
      setLoanDetails((prev: any) => ({ ...prev, documents: uniqueDocuments }));
      setDocumentList(uniqueDocuments);

      // Mark only this file as done
      setFiles((prev: any[]) =>
        prev.map((f: any, i: number) =>
          i === fileIndex ? { ...f, progress: 100, error: false } : f,
        ),
      );
    } catch (err) {
      console.error("Error while retrying document upload: ", err);
      setFiles((prev: any[]) =>
        prev.map((f: any, i: number) =>
          i === fileIndex
            ? { ...f, error: "Upload failed. Please try again." }
            : f,
        ),
      );
    } finally {
      setUploading(false);
    }
  }

  function validateForm() {
    let isError: boolean = false;
    let tempFormErrors: ObjectType = { ...(detailedLoanSizingErrors ?? {}) };
    const propErrors = [...(tempFormErrors?.propertyDetails ?? [])];
    const loanErrors: any = {
      borrowerInformation: tempFormErrors?.borrowerInformation,
      loanInformation: tempFormErrors?.loanInformation,
      loanEconomics: tempFormErrors?.loanEconomics,
    };
    detailedLoanSizingData?.propertyDetails?.forEach(
      (property: any, index: number) => {
        Object.keys(property).forEach((key: string) => {
          if (
            validatePropertySectionFields(
              key,
              property,
              index,
              propErrors,
              tempFormErrors,
              fieldConfig,
            )
          ) {
            isError = true;
          }
        });
      },
    );
    fieldConfig?.borrowerDetails?.borrowerInformation?.fields?.forEach(
      (bConfig: any) => {
        if (
          bConfig?.required &&
          bConfig?.isVisible(detailedLoanSizingData?.borrowerInformation) &&
          !detailedLoanSizingData?.borrowerInformation?.[bConfig.id]
        ) {
          isError = true;
          loanErrors.borrowerInformation = {
            ...loanErrors.borrowerInformation,
            [bConfig.id]: "thisFieldIsRequired",
          };
        }
      },
    );
    Object.keys(fieldConfig?.loanDetails).forEach((key: string) => {
      fieldConfig?.loanDetails?.[key]?.fields?.forEach((lConfig: any) => {
        if (
          lConfig?.required &&
          lConfig?.isVisible(detailedLoanSizingData?.[key]) &&
          (detailedLoanSizingData?.[key]?.[lConfig.id] === null ||
            detailedLoanSizingData?.[key]?.[lConfig.id] === undefined ||
            detailedLoanSizingData?.[key]?.[lConfig.id] === "")
        ) {
          isError = true;
          loanErrors[key] = {
            ...loanErrors[key],
            [lConfig.id]: "thisFieldIsRequired",
          };
        }
      });
    });
    fieldConfig?.borrowerDetails?.borrowerInformation?.fields?.forEach(
      (bConfig: any) => {
        if (
          bConfig?.required &&
          bConfig?.isVisible(detailedLoanSizingData?.borrowerInformation) &&
          !detailedLoanSizingData?.borrowerInformation?.[bConfig.id]
        ) {
          isError = true;
          loanErrors.borrowerInformation = {
            ...loanErrors.borrowerInformation,
            [bConfig.id]: "thisFieldIsRequired",
          };
        }
      },
    );
    setDetailedLoanSizingErrors({
      ...loanErrors,
      propertyDetails: propErrors,
    });
    return isError;
  }

  async function handleConvertion() {
    try {
      const reqBody = {
        loanId,
        sizingType: SizingTypeValue.DETAILED_LOAN_SIZING,
      };

      await convertLoanSizingData(reqBody);
      const convertedData = convertQuickToDetailedLoanData(quickLoanSizingData);
      const tempData: ObjectType = {
        orgId:
          loanDetails?.orgId || getCookie("orgId") || dynamicConfig?.clientId,
        fundingType: orgFundingType === "undefined" ? "normal" : orgFundingType,
        applicationName: applicationName || evaluationID?.toString(),
        evaluationID,
        isApplicationSubmitted: Boolean(loanDetails?.tcLoanId),
        testTabData,
        ratesAsOf: ratesAsOfDate,
        leverageAndPricingData,
        // autoCalculatedLoanResults,
        // autoCalculatedPropResults: [...autoCalculatedPropResults],
        tcLoanId: loanDetails?.tcLoanId,
        takeOutPartner: assignedPartner,
        rulesResponse,
        eligiblePartnerResults,
        partnersResults,
        ...convertedData,
      };

      await patchSaveApplication(loanId, tempData);
      setDetailedLoanSizingData({
        ...convertedData,
      });
      setLoanDetails({
        ...loanDetails,
        sizingType: SizingTypeValue?.DETAILED_LOAN_SIZING,
        detailedLoanSizingData: convertedData,
      });
      setSizingType(SizingType.DETAILED_LOAN_SIZING);
      await autoGenerateTermSheet();
    } catch (err) {
      console.error(
        "Something went wrong while converting the loan. ERROR:",
        err,
      );
    }
  }

  const autoGenerateTermSheet = async () => {
    await getTermSheetDataBasedOnAction("autogenerate");
  };

  // Builds the loanData payload used by handleSaveApplication, based on the
  // current sizingType. Extracted from handleSaveApplication to reduce its
  // cognitive complexity.
  function buildSaveApplicationLoanData() {
    let loanData: any = {};
    if (loanDetails?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING) {
      loanData = {
        borrowerInformation: quickLoanSizingData?.borrowerInformation || {},
        loanInformation: {
          loanPurpose: quickLoanSizingData?.loanInformation?.loanPurpose,
          originationDate: quickLoanSizingData?.loanInformation
            ?.expectedClosingDate
            ? getFormattedDate(
                quickLoanSizingData?.loanInformation?.expectedClosingDate,
              )
            : null,
        },
        loanEconomics: {
          ...quickLoanSizingData?.loanEconomics,
          borrowerRequested:
            quickLoanSizingData?.loanInformation?.borrowerRequested,
          purchasePrice: quickLoanSizingData?.loanInformation?.purchasePrice,
        },
        loanOutputs: quickLoanSizingData?.loanOutputs || {},
        propertyDetails: [
          {
            propertyLocation: quickLoanSizingData?.propertyLocation || {},
            propertyInformation: quickLoanSizingData?.propertyInformation || {},
            propertyEconomics: quickLoanSizingData?.propertyEconomics || {},
          },
        ],
      };
    }
    if (loanDetails?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING) {
      loanData = {
        ...detailedLoanSizingData,
        loanInformation: {
          ...detailedLoanSizingData?.loanInformation,
          originationDate: detailedLoanSizingData?.loanInformation
            ?.expectedClosingDate
            ? getFormattedDate(
                detailedLoanSizingData?.loanInformation?.expectedClosingDate,
              )
            : null,
          expectedClosingDate: null,
        },
      };
    }
    return loanData;
  }

  // Applies the post-save side effects when patchSaveApplication succeeds:
  // audit flush, save-confirmation messaging, and refetching the loan.
  // Extracted from handleSaveApplication to reduce its cognitive complexity.
  async function applySaveApplicationSuccessSideEffects(
    tempData: ObjectType,
    isSubmittedApp: boolean,
    additionalStr?: string,
  ) {
    if (dynamicConfig?.enableAudit) {
      await flushPendingAuditEdits({
        loanId,
        tempData,
        isFirstEverAudit,
        pendingAuditEdits,
        decodedTokenData,
        realmHeaders,
        setAuditedFieldPaths,
        setFieldAuditHistoryCache,
        setPendingAuditEdits,
        setIsFirstEverAudit,
        refreshAuditedFields: fetchAndApplyAuditData,
      });
    }
    if (!isSubmittedApp && !loanDetails?.tcLoanId) {
      const str = additionalStr
        ? `${t(additionalStr)} and ${t("changesSaved")}`
        : t("changesSaved");
      setSnackBarMsg(str);
      setShowSnackBar(true);
    } else if (loanDetails?.tcLoanId) {
      const str = `${t("changesSaved")}\n \n ${t(
        "toastAfterSaveForSubmitted",
      )}`;
      setSnackBarMsg(str);
      setShowSnackBar(true);
    }
    const body = [loanId];
    const { data } = await loanDataBasedOnLoanId(body);
    setLoansApiResponse(data?.data["0"]);
    setLoanDetails(data?.data?.["0"]?.data);
    // loanDataBasedOnLoanId returns the loan document only — it never
    // carries waiverDetails/generalWaiverDetails (the waiver service is the
    // sole persistence layer for those, see fetchAndSyncWaivers). The plain
    // setLoanDetails above therefore drops whatever waiver data the initial
    // mount-time fetchAndSyncWaivers call had merged in, which is why a
    // save/escalate right after requesting a waiver made the "Request
    // Waiver" button reappear instead of "Pending Review". Re-sync here,
    // same as on initial load.
    fetchAndSyncWaivers(loanId, setLoanDetails, setWaiverDecisionVersion);
    setApplicationName(applicationName || (evaluationID?.toString() ?? ""));
  }

  // Resolves loanType/loanSizerType for the waiver service's
  // update-approved-waivers endpoint. Deliberately not sharing
  // resolveLoanTypeAndSizerType (utils/Common.ts) — that one targets the TC
  // submission payload ("30 Year Loan"/"BridgeLoan"), a different consumer
  // with different expected values. fetchData?.loanType is the Bridge
  // sub-type already used throughout this page ("Ground Up", "Rehab", "Fix &
  // Flip", "Stabilized", "MultiFamily"); only "Ground Up" is known to need
  // reformatting to "Ground-Up" here — confirm any other casing with the
  // waiver-service team before relying on it.
  function resolveWaiverUpdateLoanType(): {
    loanType: string;
    loanSizerType?: string | null;
  } {
    if (loanType === "InvestorDSCR") {
      return { loanType: "30year", loanSizerType: "" };
    }
    return {
      loanType: "Bridge",
      loanSizerType:
        fetchData?.loanType === "Ground Up" ? "Ground-Up" : fetchData?.loanType,
    };
  }

  async function handleSaveApplication(
    isApplicationSubmit?: boolean,
    tempTcLoanId?: string,
    additionalStr?: string,
    currentInterestRate?: any,
    skipSubmitCheck?: boolean,
  ) {
    const isSubmittedApp = Boolean(tempTcLoanId);
    const tempLoanId = tempTcLoanId || tcLoanId || "";
    if (!applicationName && !isSubmittedApp && !skipSubmitCheck) {
      setApplicationError(t("thisFieldIsRequired"));
      return;
    }
    setIsLoading(true);
    try {
      const loanData: any = buildSaveApplicationLoanData();
      const currentRateData: any =
        currentInterestRate ?? currentSelectedInterestRate;
      if (currentInterestRate) {
        loanData.loanEconomics.interestRate = Number(
          currentInterestRate?.interestRate?.replace("%", ""),
        );
      }
      loanData.loanEconomics.originationPoints =
        leverageAndPricingData?.leverageCards?.originationPoints;
      // Nested under loanEconomics (not a top-level tempData field) so it
      // rides along with the same round-trip interestRate/finalToorakPrice
      // already rely on. Explicitly null (not omitted/empty object) once
      // cleared, so a stale override never lingers on the saved record
      // after some other field's edit clears it locally
      // (clearStalePricingOverrides). Keys get "." swapped for a safe
      // placeholder before saving (see encodePricingAdjustmentOverrides) —
      // every row label contains a literal "." and a dot-path-aware
      // storage layer would otherwise explode it into a nested structure.
      loanData.loanEconomics.pricingAdjustmentOverrides =
        encodePricingAdjustmentOverrides(pricingAdjustmentOverrides);
      loanData.loanEconomics.leverageAdjustedMaxOverrides =
        encodeLeverageAdjustedMaxOverrides(leverageAdjustedMaxOverrides);
      // Credit Committee manual pricing (DSCR-only fields, gated in
      // LoanEconomics.ts). If CC just changed interestRate/finalToorakPrice
      // in this save, stamp it valid; otherwise leave patchSaveApplication's
      // default behavior (invalidate any previously-valid pricing) in place.
      const pricingChangedByCreditCommittee =
        isCreditCommittee &&
        (loanData?.loanEconomics?.interestRate !==
          loanDetails?.loanEconomics?.interestRate ||
          loanData?.loanEconomics?.finalToorakPrice !==
            loanDetails?.loanEconomics?.finalToorakPrice);
      if (pricingChangedByCreditCommittee) {
        loanData.loanEconomics = {
          ...loanData.loanEconomics,
          isPricingValid: true,
          pricingEnteredBy:
            decodedTokenData?.name ||
            decodedTokenData?.email ||
            decodedTokenData?.user_id ||
            "Unknown User",
          pricingEnteredOn: new Date().toISOString(),
        };
      }

      const tempData: ObjectType = {
        orgId:
          loanDetails?.orgId || getCookie("orgId") || dynamicConfig?.clientId,
        fundingType: orgFundingType === "undefined" ? "normal" : orgFundingType,
        applicationName: applicationName || evaluationID?.toString(),
        evaluationID,
        isApplicationSubmitted:
          Boolean(loanDetails?.tcLoanId) || isSubmittedApp,
        testTabData,
        ratesAsOf: ratesAsOfDate,
        leverageAndPricingData,
        autoCalculatedLoanResults,
        autoCalculatedPropResults: [...(autoCalculatedPropResults ?? [])],
        tcLoanId: tempLoanId,
        takeOutPartner: assignedPartner,
        rulesResponse,
        eligiblePartnerResults,
        partnersResults,
        buyDownOptions,
        currentSelectedInterestRate: currentRateData,
        ...loanData,
      };

      const res = await patchSaveApplication(
        loanId,
        tempData,
        pricingChangedByCreditCommittee,
      );
      setOpenSaveApplicationModal(false);
      setShowLoanDetails(true);
      if (res?.data) {
        await applySaveApplicationSuccessSideEffects(
          tempData,
          isSubmittedApp,
          additionalStr,
        );
        // Side effect of saving, not a precondition for it (same rationale
        // as mapWaiverLoanIdToDestination) — never awaited, and wrapped in
        // its own try/catch so a bad snapshot (buildWaiverUpdateRequestData
        // diffing loanDetails/detailedLoanSizingData) can never surface as a
        // "changes failed to save" error for a save that actually succeeded.
        try {
          const { loanType: waiverLoanType, loanSizerType } =
            resolveWaiverUpdateLoanType();
          const { loanData: waiverLoanData, propertyData: waiverPropertyData } =
            buildWaiverUpdateRequestData({
              loanDetails,
              detailedLoanSizingData,
              dynamicConfig,
              loanId,
              loanStage: "FES",
            });
          refreshWaiversAfterSave({
            loanId,
            waiverDetails: loanDetails?.waiverDetails,
            rulesResponse,
            loanData: waiverLoanData,
            propertyData: waiverPropertyData,
            loanStage: "FES",
            loanType: waiverLoanType,
            loanSizerType,
            isTestStillFailing: isWaivedTestStillFailing,
            setLoanDetails,
            setWaiverDecisionVersion,
          });
        } catch (waiverRefreshErr) {
          console.error(waiverRefreshErr);
        }
      } else {
        setShowSnackBar(false);
        setSnackBarMsg("changesFailedSave");
      }
    } catch (err) {
      console.error(err);
      setShowSnackBar(false);
      setSnackBarMsg("changesFailedSave");
    } finally {
      setIsLoading(false);
    }
  }

  // Also writes the new value directly into leverageAndPricingData.pricing's
  // matching row — not just pricingAdjustmentOverrides — so the loan data
  // itself is immediately consistent with what's on screen. Without this,
  // leverageAndPricingData (which gets saved as part of the loan record)
  // would still hold the pre-edit value until the debounced re-evaluation
  // this triggers actually completes — a real gap if the user clicks Save
  // Application before that finishes.
  const handleSavePricingAdjustmentRow = useCallback(
    (rowLabel: string, newValue: string) => {
      setPricingAdjustmentOverrides?.((prev: Record<string, string>) => ({
        ...prev,
        [rowLabel]: newValue,
      }));
      setPendingAuditEdits?.((prev: any) => ({
        ...prev,
        [getPricingAdjustmentAuditFieldPath(rowLabel)]: {
          value: newValue,
          comment: DEFAULT_AUDIT_COMMENT,
        },
      }));
      setLeverageAndPricingData?.((prev: any) =>
        computeUpdatedLeveragesTableBody(
          prev,
          leverageTestsConfig,
          rowLabel,
          newValue,
        ),
      );
      setNewDebouncedValue?.(newValue);
    },
    [
      setPricingAdjustmentOverrides,
      setPendingAuditEdits,
      setLeverageAndPricingData,
      setNewDebouncedValue,
    ],
  );

  // Extracted from a top-level const to a function (called from
  // getGridHeadingActions) to reduce the cognitive complexity of
  // LoansPageV2 itself.
  function renderPricingHeadingActions() {
    return canUpdatePricingAdjustment ? (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {isEditingPricingCard ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                <PricingCancelIcon style={{ width: 14, height: 14 }} />
              }
              onClick={handleCancelPricingEdit}
              sx={pricingActionButtonSx}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PricingSaveIcon style={{ width: 14, height: 14 }} />}
              onClick={handleSavePricingEdit}
              sx={pricingActionButtonSx}
            >
              {t("save")}
            </Button>
          </>
        ) : (
          <Tooltip title={t("editPricingAdjustments")}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PricingEditIcon style={{ width: 14, height: 14 }} />}
              onClick={handleEditPricingClick}
              sx={pricingActionButtonSx}
            >
              {t("edit")}
            </Button>
          </Tooltip>
        )}
      </div>
    ) : null;
  }

  const handleSaveLeveragesAdjustmentRow = useCallback(
    (columnKey: string, newValue: string) => {
      setLeverageAdjustedMaxOverrides?.((prev: Record<string, string>) => ({
        ...prev,
        [columnKey]: newValue,
      }));
      setPendingAuditEdits?.((prev: any) => ({
        ...prev,
        [getLeverageAdjustmentAuditFieldPath(columnKey)]: {
          value: newValue,
          comment: DEFAULT_AUDIT_COMMENT,
        },
      }));
      setLeverageAndPricingData?.((prev: any) =>
        computeUpdatedLeveragesTableBody(
          prev,
          leverageTestsConfig,
          columnKey,
          newValue,
        ),
      );
      setNewDebouncedValue?.(newValue);
    },
    [
      setLeverageAdjustedMaxOverrides,
      setPendingAuditEdits,
      setLeverageAndPricingData,
      setDetailedLoanSizingData,
      leverageTestsConfig,
      setNewDebouncedValue,
    ],
  );


  const handleSaveLeveragesEdit = useCallback(() => {
    let hasChangedRow = false;
    Object.keys(leveragesDraftValues).forEach((columnKey) => {
      if (leveragesDraftValues[columnKey] !== leveragesOriginalValues[columnKey]) {
        hasChangedRow = true;
        handleSaveLeveragesAdjustmentRow(columnKey, leveragesDraftValues[columnKey]);
      }
    });
    setIsEditingLeveragesCard(false);
    setLeveragesDraftValues({});
    setLeveragesOriginalValues({});
    // Persist the edit to the loan record itself, not just local state —
    // see the effect watching pendingLeveragesApplicationSave for why this
    // can't just call handleSaveApplication directly from here.
    if (hasChangedRow) {
      setPendingLeveragesApplicationSave(true);
    }
  }, [leveragesDraftValues, leveragesOriginalValues, handleSaveLeveragesAdjustmentRow]);

  const handleLeveragesDraftChange = useCallback((columnKey: string, value: string) => {
    setLeveragesDraftValues((prev) => ({ ...prev, [columnKey]: value }));
  }, []);

  // Extracted from a top-level const to a function (called from
  // getGridHeadingActions) to reduce the cognitive complexity of
  // LoansPageV2 itself.
  function renderLeveragesHeadingActions() {
    return canUpdatePricingAdjustment ? (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {isEditingLeveragesCard ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                <PricingCancelIcon style={{ width: 14, height: 14 }} />
              }
              onClick={handleCancelLeveragesEdit}
              sx={pricingActionButtonSx}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PricingSaveIcon style={{ width: 14, height: 14 }} />}
              onClick={handleSaveLeveragesEdit}
              sx={pricingActionButtonSx}
            >
              {t("save")}
            </Button>
          </>
        ) : (
          <Tooltip title={t("editLeverageAdjustments")}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PricingEditIcon style={{ width: 14, height: 14 }} />}
              onClick={handleEditLeveragesClick}
              sx={pricingActionButtonSx}
            >
              {t("edit")}
            </Button>
          </Tooltip>
        )}
      </div>
    ) : null;
  }

  const handleEditLeveragesClick = useCallback(() => {
    const rows: any[] = leverageAndPricingData?.leverages?.tableBody ?? [];
    const adjustedMaxRow = rows.find((row: any) => row?.id === "adjustedMax");
    const headerCols: any[] =
      leverageTestsConfig?.find((ele: ObjectType) => ele?.label === "leverages")?.tableHeader ?? [];
    const seeded: Record<string, string> = {};
    adjustedMaxRow?.data?.forEach((value: any, idx: number) => {
      if (idx === 0) return;
      const columnKey = headerCols?.[idx]?.label;
      if (!columnKey) return;
      seeded[columnKey] = leverageAdjustedMaxOverrides?.[columnKey] ?? String(value ?? "");
    });
    setLeveragesDraftValues(seeded);
    setLeveragesOriginalValues(seeded);
    setIsEditingLeveragesCard(true);
  }, [leverageAndPricingData, leverageTestsConfig, leverageAdjustedMaxOverrides]);

  const handleCancelLeveragesEdit = useCallback(() => {
    setIsEditingLeveragesCard(false);
    setLeveragesDraftValues({});
    setLeveragesOriginalValues({});
  }, []);

  // Edit-mode handlers for the Pricing card's header actions (see
  // pricingHeadingActions below).
  const handleEditPricingClick = useCallback(() => {
    const rows: any[] = leverageAndPricingData?.pricing?.tableBody ?? [];
    const seeded: Record<string, string> = {};
    rows.forEach((row: any[]) => {
      if (row?.[0] && row[0] !== "Final Price Adjustment") {
        seeded[row[0]] =
          pricingAdjustmentOverrides?.[row[0]] ?? String(row[1] ?? "");
      }
    });
    setPricingDraftValues(seeded);
    setPricingOriginalValues(seeded);
    setIsEditingPricingCard(true);
  }, [leverageAndPricingData, pricingAdjustmentOverrides]);

  const handleCancelPricingEdit = useCallback(() => {
    setIsEditingPricingCard(false);
    setPricingDraftValues({});
    setPricingOriginalValues({});
  }, []);

  const handleSavePricingEdit = useCallback(() => {
    let hasChangedRow = false;
    Object.keys(pricingDraftValues).forEach((rowLabel) => {
      if (pricingDraftValues[rowLabel] !== pricingOriginalValues[rowLabel]) {
        hasChangedRow = true;
        handleSavePricingAdjustmentRow(rowLabel, pricingDraftValues[rowLabel]);
      }
    });
    setIsEditingPricingCard(false);
    setPricingDraftValues({});
    setPricingOriginalValues({});
    // Persist the edit to the loan record itself, not just local state —
    // see the effect watching pendingPricingApplicationSave for why this
    // can't just call handleSaveApplication directly from here.
    if (hasChangedRow) {
      setPendingPricingApplicationSave(true);
    }
  }, [
    pricingDraftValues,
    pricingOriginalValues,
    handleSavePricingAdjustmentRow,
  ]);

  const handlePricingDraftChange = useCallback(
    (rowLabel: string, value: string) => {
      setPricingDraftValues((prev) => ({ ...prev, [rowLabel]: value }));
    },
    [],
  );

  const pricingActionButtonSx = {
    textTransform: "none",
    minWidth: 0,
    padding: "2px 8px",
    fontSize: 12,
    lineHeight: 1.5,
    "& .MuiButton-startIcon": { marginRight: "4px" },
  };

    const fetchAndApplyAuditData = async () => {
    if (!dynamicConfig?.enableAudit) return;
    try {
      const { data: auditData } = await fetchAuditedFields(
        [{ resourceType: AUDIT_RESOURCE_TYPE, resourceId: loanId ?? "" }],
        realmHeaders,
      );
      const auditedPaths = extractAuditedFieldPaths(auditData, loanId ?? "");
      setAuditedFieldPaths?.(auditedPaths);
      setIsFirstEverAudit?.(!hasAnyAuditEntry(auditData, loanId ?? ""));
    } catch (auditErr) {
      console.error(auditErr);
    }
  };

  const getLoanDataBasedOnLoanId = async () => {
    setDynamicLoader(true);
    try {
      const body = [loanId];
      const { data } = await loanDataBasedOnLoanId(body);
      if (dynamicConfig?.enableAudit) {
        try {
          const { data: auditData } = await fetchAuditedFields(
            [{ resourceType: AUDIT_RESOURCE_TYPE, resourceId: loanId ?? "" }],
            realmHeaders,
          );
          const auditedPaths = extractAuditedFieldPaths(
            auditData,
            loanId ?? "",
          );
          setAuditedFieldPaths?.(auditedPaths);
          setIsFirstEverAudit?.(!hasAnyAuditEntry(auditData, loanId ?? ""));
        } catch (auditErr) {
          console.error(auditErr);
        }
      }
      const keysToBeChecked = [
        "loanInformation",
        "loanEconomics",
        "propertyDetails",
        "borrowerInformation",
      ];

      if (
        data?.data?.["0"]?.data?.sizingType ===
        SizingTypeValue?.QUICK_LOAN_SIZING
      ) {
        let reqBody: any = {};
        // const keys = ["borrowerInformation", "loanInformation", "loanEconomics", "propertyDetails"];
        if (
          !keysToBeChecked.some((key) =>
            data?.data?.["0"]?.data?.hasOwnProperty(key),
          )
        ) {
          reqBody = {
            loanInformation: {
              ...intialDetailedLoanSizingState.loanInformation,
            },
            propertyInformation: {
              propertyType:
                data?.data?.["0"]?.data?.propertyType ===
                "Single Family/Detached"
                  ? "SFR"
                  : data?.data?.["0"]?.data?.propertyType,
            },
          };
        } else {
          reqBody = {
            borrowerInformation: data?.data?.["0"]?.data?.borrowerInformation,
            loanInformation: {
              ...data?.data?.["0"]?.data?.loanInformation,
              borrowerRequested:
                data?.data?.["0"]?.data?.loanEconomics?.borrowerRequested,
              purchasePrice:
                data?.data?.["0"]?.data?.loanEconomics?.purchasePrice,
              expectedClosingDate:
                data?.data?.["0"]?.data?.loanInformation?.originationDate,
            },
            loanEconomics: data?.data?.["0"]?.data?.loanEconomics,
            propertyLocation:
              data?.data?.["0"]?.data?.propertyDetails?.[0]?.propertyLocation,
            propertyInformation:
              data?.data?.["0"]?.data?.propertyDetails?.[0]
                ?.propertyInformation,
            propertyEconomics:
              data?.data?.["0"]?.data?.propertyDetails?.[0]?.propertyEconomics,
          };
        }
        setQuickLoanSizingData(reqBody ?? {});
      } else if (
        data?.data?.["0"]?.data?.sizingType ===
        SizingTypeValue?.DETAILED_LOAN_SIZING
      ) {
        let reqBody: any = {};
        if (
          !keysToBeChecked.some((key) =>
            data?.data?.["0"]?.data?.hasOwnProperty(key),
          )
        ) {
          reqBody = {
            loanInformation: intialDetailedLoanSizingState.loanInformation,
            loanEconomics: intialDetailedLoanSizingState.loanEconomics,
            propertyDetails: [
              {
                propertyInformation: {
                  propertyType:
                    data?.data?.["0"]?.data?.propertyType ===
                    "Single Family/Detached"
                      ? "SFR"
                      : data?.data?.["0"]?.data?.propertyType,
                },
                propertyEconomics: {
                  decliningMarkets: "No",
                },
                unitInformation: [{}],
              },
            ],
          };
        } else {
          reqBody = {
            borrowerInformation: {
              ...data?.data?.["0"]?.data?.borrowerInformation,
              ...detailedLoanSizingData.borrowerInformation,
            },
            loanInformation: {
              ...data?.data?.["0"]?.data?.loanInformation,
              ...detailedLoanSizingData.loanInformation,
              expectedClosingDate:
                data?.data?.["0"]?.data?.loanInformation?.originationDate,
            },
            loanEconomics: {
              ...data?.data?.["0"]?.data?.loanEconomics,
              ...detailedLoanSizingData.loanEconomics,
              ...data?.data?.["0"]?.data?.autoCalculatedLoanResults
                ?.loanEconomics,
              // Always the persisted record's rate on (re)load, never
              // whatever detailedLoanSizingData happened to hold before
              // this fetch resolved (its initial/stale value, spread above,
              // would otherwise silently win and wipe out a previously
              // selected buy-down or CC-entered rate the very first time
              // this loan's data loads). handleSaveApplication writes both
              // a buy-down selection and a CC edit through this same
              // loanEconomics.interestRate field, so the saved value
              // already reflects whichever happened most recently.
              interestRate: data?.data?.["0"]?.data?.loanEconomics
                ?.interestRate,
              armIndex: "1 Month Term SOFR",
              prepaymentPenaltyTypeMonths:
                data?.data?.["0"]?.data?.autoCalculatedLoanResults
                  ?.loanEconomics?.prepaymentPenaltyTypeMonths ||
                data?.data?.["0"]?.data?.loanEconomics
                  ?.prepaymentPenaltyTypeMonths,
              prepayPenaltyType:
                data?.data?.["0"]?.data?.autoCalculatedLoanResults
                  ?.loanEconomics?.prepayPenaltyType ||
                data?.data?.["0"]?.data?.loanEconomics?.prepayPenaltyType,
            },
            propertyDetails: data?.data?.["0"]?.data?.propertyDetails?.map(
              (property: any) => {
                return {
                  propertyLocation: property?.propertyLocation ?? {},
                  propertyInformation: property?.propertyInformation ?? {},
                  propertyEconomics: {
                    ...property?.propertyEconomics,
                    costBasisCalculated:
                      +(property?.propertyEconomics?.purchasePrice || 0) +
                      +(
                        property?.propertyEconomics
                          ?.verifiedCostsAndImprovements || 0
                      ),
                  },
                  unitInformation: property?.unitInformation ?? [],
                };
              },
            ) ?? [
              {
                propertyLocation: {},
                propertyInformation: {},
                propertyEconomics: {},
                unitInformation: [{}],
              },
            ],
          };
        }
        setDetailedLoanSizingData(reqBody || intialDetailedLoanSizingState);
      }

      setEvaluationID(data?.data?.["0"]?.data?.evaluationID);
      setSizingType(
        data?.data?.["0"]?.data?.sizingType ===
          SizingTypeValue?.QUICK_LOAN_SIZING
          ? SizingType.QUICK_LOAN_SIZING
          : SizingType.DETAILED_LOAN_SIZING,
      );
      const findCurrentSelection = data?.data?.[
        "0"
      ]?.data?.buyDownOptions?.find(
        (option: any) =>
          option.interestRate ===
          Number(
            data?.data?.[
              "0"
            ]?.data?.currentSelectedInterestRate?.interestRate?.replace(
              "%",
              "",
            ) / 100,
          ),
      );
      const newCurrentSelectedInterestRate = findCurrentSelection
        ? {
            interestRate: `${(
              findCurrentSelection?.interestRate * 100
            )?.toFixed(3)}%`,
            discountPoints: `${findCurrentSelection?.discountPointPercentage?.toFixed(
              4,
            )}`,
            discountPointsWithDollar: formatCurrency(
              findCurrentSelection?.discountPointAmount,
            ),
            totalBuyYield: findCurrentSelection?.totalBuyYield,
          }
        : data?.data?.["0"]?.data?.currentSelectedInterestRate;
      setCurrentSelectedInterestRate(newCurrentSelectedInterestRate);
      if (data?.data?.["0"]?.data?.tcLoanId) {
        setLeverageAndPricingData(
          data?.data?.["0"]?.data?.leverageAndPricingData,
        );
      }
      setFetchData(data?.data["0"]?.data || {});
      setAutoCalculatedLoanResults(
        data?.data?.["0"]?.data?.autoCalculatedLoanResults,
      );
      setAutoCalculatedPropResults(
        data?.data?.["0"]?.data?.autoCalculatedPropResults,
      );
      setLoanDetails(data?.data?.["0"]?.data);
      fetchAndSyncWaivers(loanId, setLoanDetails, setWaiverDecisionVersion);
      const rehydratedPricingAdjustmentOverrides =
        decodePricingAdjustmentOverrides(
          data?.data?.["0"]?.data?.loanEconomics?.pricingAdjustmentOverrides,
        );
      setPricingAdjustmentOverrides?.(rehydratedPricingAdjustmentOverrides);
      const rehydratedLeverageAdjustedMaxOverrides =
        decodeLeverageAdjustedMaxOverrides(
          data?.data?.["0"]?.data?.loanEconomics?.leverageAdjustedMaxOverrides,
        );
      setLeverageAdjustedMaxOverrides?.(rehydratedLeverageAdjustedMaxOverrides);
      if (
        Object.keys(rehydratedPricingAdjustmentOverrides).length ||
        Object.keys(rehydratedLeverageAdjustedMaxOverrides).length
      ) {
        // Guards against a race where the page's automatic on-load
        // evaluation completes before this rehydration finishes — forces a
        // fresh evaluation so a page refresh always ends up sending the
        // current overrides, not the pre-override natural values.
        setNewDebouncedValue?.(Date.now());
      }
      if (Object.keys(rehydratedLeverageAdjustedMaxOverrides).length) {
        setNewDebouncedValue?.(Date.now());
      }
      setTcLoanId(data?.data?.["0"]?.data?.tcLoanId);
      setApplicationName(data?.data?.["0"]?.data?.applicationName);
      setDocumentList(data?.data?.["0"]?.data?.documents ?? []);
      const condoData = {
        ...(data?.data["0"]?.data?.loanCondominium ?? sampleCondoEligibility),
        ["borrowerName"]: decodedTokenData?.name,
      };
      setCondoEligibilityFormData(condoData);
    } catch (err) {
      console.error(err);
    } finally {
      setDynamicLoader(false);
    }
  };

  const handlePromptSubmit = async () => {
    const { data } = await fetchPricingExceptionsForLoan(
      params.loanId,
      buyDownOptions,
    );

    const activeWaiver = data?.data
      ?.map((item: any, index: number) => ({ ...item, originalIndex: index }))
      ?.filter(
        (item: any) =>
          item?.data?.isActive && item?.data?.waiverStatus === "Requested",
      );
    if (activeWaiver?.length) {
      setShowWarningForWaiversSubmit(true);
    }
    setOpenWarningBeforeSubmit(true);

    const hasEverBeenPriced = Boolean(
      loanDetails?.loanEconomics?.pricingEnteredOn,
    );
    // Stale CC pricing only actually blocks submission while the loan is
    // still otherwise not ready (no assigned partner, due to failing
    // eligibility/pricing tests) — once a partner is assigned and tests
    // pass, the loan no longer needs a valid CC override at all, so a
    // previously-entered-then-invalidated price shouldn't hold it up.
    const pricingStale =
      hasEverBeenPriced &&
      loanDetails?.loanEconomics?.isPricingValid === false &&
      noAssignedPartner === "eligibilityAndPricingText";
    setIsPricingStale(pricingStale);

    // Read from detailedLoanSizingData, not loanDetails — unlike isPricingValid
    // above (which only needs to reflect the last-loaded/last-saved record),
    // this has to catch an override going stale from an edit made earlier in
    // THIS session: handleSavePricingAdjustmentRow/handleSaveLeveragesAdjustmentRow
    // and clearStalePricingOverrides both write isPricingAdjustmentValid/
    // isLeverageAdjustmentValid onto detailedLoanSizingData synchronously as
    // the user edits, while loanDetails only gets refreshed on specific
    // events (page load, partner assignment, etc).
    const leveragePricingOverrideStale =
      detailedLoanSizingData?.loanEconomics?.isPricingAdjustmentValid ===
        false ||
      detailedLoanSizingData?.loanEconomics?.isLeverageAdjustmentValid ===
        false;
    setIsLeveragePricingOverrideStale(leveragePricingOverrideStale);

    const isFormError = validateForm();
    const isError = isFormError || pricingStale || leveragePricingOverrideStale;
    setIsError(isError);
    if (isError) return;
  };

  function getHeadingText(text: ObjectType, card: ObjectType) {
    const isProfitable = Boolean(text?.profitability);
    const profitabilityTest = Boolean(
      testTabData?.find(
        (test: any) =>
          test.title === "Profitability Test" && test.status === "Failed",
      ),
    );
    let returnValue = (
      <span className={isProfitable && profitabilityTest ? "red-text" : ""}>
        {text?.[card.label]}
      </span>
    );

    if (card.label === "finalRate") {
      returnValue = (
        <span>
          {failedFinalRate ? (
            <img src={AlertTriangle} alt="failed.svg" />
          ) : null}
          {text?.[card.label]}
        </span>
      );
    }

    if (
      card.label === "interestRate" &&
      loanDetails?.loanEconomics?.pricingEnteredOn
    ) {
      const isPricingStale =
        loanDetails?.loanEconomics?.isPricingValid === false;
      // Reads the live edited value, not loanDetails (the last
      // successfully-saved/evaluated server record) — loanDetails only
      // refreshes after a full round-trip succeeds (a valid guideline
      // result with max loan amount/assigned partner), so right after a CC
      // keystroke, or after some other field edit clears this override
      // pending a fresh evaluation, loanDetails.loanEconomics.interestRate
      // still holds a stale value (the old rate, or one that's since been
      // cleared) until that round-trip completes — this card would keep
      // showing that stale value instead of what's actually in the input.
      const currentInterestRate =
        detailedLoanSizingData?.loanEconomics?.interestRate ??
        loanDetails.loanEconomics.interestRate;
      returnValue = (
        <span>
          {isPricingStale ? (
            <img
              src={AlertTriangle}
              alt="stale.svg"
              title={t("pricingNoLongerApplicable")}
            />
          ) : null}
          {`${currentInterestRate}%`}
        </span>
      );
    }

    if (card.label === "failedTests") {
      const value = text?.[card.label];
      returnValue = (
        <span style={{ color: value && +value > 0 ? "#f75676" : "#2ece89" }}>
          {value}
        </span>
      );
    }
    return returnValue;
  }

  // The next several helpers are shared by renderGridLayout and
  // renderRightPanel, which render near-identical leverage/pricing table
  // cards — extracted to reduce the cognitive complexity of both.
  function getGridHeadingActions(label: string) {
    return label === "pricing"
      ? renderPricingHeadingActions()
      : label === "leverages"
        ? renderLeveragesHeadingActions()
        : undefined;
  }

  function isLeverageTableEditable(label: string) {
    return (
      (label === "pricing" || label === "leverages") &&
      canUpdatePricingAdjustment
    );
  }

  function getIsEditingLeverageCard(label: string) {
    return label === "pricing"
      ? isEditingPricingCard
      : label === "leverages"
        ? isEditingLeveragesCard
        : false;
  }

  function getLeverageDraftValues(label: string) {
    return label === "leverages" ? leveragesDraftValues : pricingDraftValues;
  }

  function getLeverageOnDraftChange(label: string) {
    return label === "leverages"
      ? handleLeveragesDraftChange
      : handlePricingDraftChange;
  }

  // Extracted from renderGridLayout's "disclaimer" case to reduce its
  // cognitive complexity.
  function renderGridDisclaimerCard(gridEle: any) {
    return loanDetails?.ratesAsOf || ratesAsOfDate ? (
      <Grid container xs={12} key={`grid-${gridEle.label}`}>
        <Grid item xs={12} m={expandLeverageAndTests ? 0 : 2}>
          <Disclaimer
            boldText={""}
            children={
              loanType === "InvestorDSCR" ? (
                <span>
                  {t("ratesAsOfDate")} &nbsp;
                  <b>
                    {moment(
                      loanDetails?.ratesAsOf || ratesAsOfDate,
                      "DD-MM-YYYY",
                    ).format("MMM DD, YYYY")}
                  </b>
                  &nbsp;
                  {t("daysLockPeriod")}
                </span>
              ) : (
                <span>
                  {t("pricingAsOfDate")} &nbsp;
                  <b>
                    {moment(
                      loanDetails?.ratesAsOf || ratesAsOfDate,
                      "DD-MM-YYYY",
                    ).format("MMM DD, YYYY")}
                  </b>
                </span>
              )
            }
            customClass="disclaimerBox"
            showClose={false}
          />
        </Grid>
      </Grid>
    ) : null;
  }

  // Extracted from renderRightPanel's "disclaimer" case to reduce its
  // cognitive complexity.
  function renderRightPanelDisclaimerCard(ele: any, idx: number) {
    return loanDetails?.ratesAsOf || ratesAsOfDate ? (
      <Grid container key={`grid-${ele.label}-${idx}`}>
        <Grid item xs={expandLeverageAndTests ? 6 : 12} m={2}>
          <Disclaimer
            boldText={""}
            children={
              loanType === "InvestorDSCR" ? (
                <span>
                  {t("ratesAsOfDate")} &nbsp;
                  <b>
                    {moment(
                      Boolean(loanDetails?.tcLoanId)
                        ? loanDetails?.ratesAsOf
                        : ratesAsOfDate || loanDetails?.ratesAsOf,
                      "DD-MM-YYYY",
                    ).format("MMM DD, YYYY")}
                  </b>
                  &nbsp;
                  {t("daysLockPeriod")}
                </span>
              ) : (
                <span>
                  {t("pricingAsOfDate")} &nbsp;
                  <b>
                    {moment(
                      loanDetails?.ratesAsOf || ratesAsOfDate,
                      "DD-MM-YYYY",
                    ).format("MMM DD, YYYY")}
                  </b>
                </span>
              )
            }
            customClass="disclaimerBox"
            showClose={false}
            isRatesAsOff={true}
          />
        </Grid>
      </Grid>
    ) : null;
  }

  function renderGridLayout(gridEle: any) {
    switch (gridEle?.type) {
      case "table":
        return (
          <div style={{ margin: "15px 0" }} key={`grid-${gridEle.label}`}>
            <CustomCard
              headingText={t(gridEle.label)}
              headingActions={getGridHeadingActions(gridEle.label)}
              subHeadingText={""}
              loanDetails={loanDetails}
              fullView={gridEle?.fullView ?? false}
              ratesAsOfDate={ratesAsOfDate}
              subComponents={
                <LeverageTable
                  tableHeader={gridEle?.tableHeader ?? []}
                  tableBody={leverageAndPricingData[gridEle.label]?.tableBody}
                  label={gridEle.label}
                  tableClassName={gridEle?.tableClassName ?? ""}
                  editable={isLeverageTableEditable(gridEle.label)}
                  overrides={pricingAdjustmentOverrides}
                  leverageOverrides={leverageAdjustedMaxOverrides}
                  isEditingCard={getIsEditingLeverageCard(gridEle.label)}
                  draftValues={getLeverageDraftValues(gridEle.label)}
                  onDraftChange={getLeverageOnDraftChange(gridEle.label)}
                />
              }
              gridSpace={4}
              data={leverageAndPricingData?.[gridEle.id]}
              headerClass={gridEle?.headerClass ?? ""}
            />
          </div>
        );
      case "disclaimer":
        return renderGridDisclaimerCard(gridEle);
      default:
        return (
          <Grid
            key={`grid-${gridEle.label}`}
            container
            my={2}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {gridEle?.cards
              ?.filter(
                (card: ObjectType) => !card?.hide?.(leverageAndPricingData),
              )
              ?.map((card: any) => (
                <Grid
                  key={`card-${card?.label}`}
                  item
                  style={{
                    flexGrow: "1",
                    maxWidth: card?.width || "55%",
                    margin: "4px",
                  }}
                >
                  <CustomCard
                    headingText={getHeadingText(
                      leverageAndPricingData?.[gridEle.id],
                      card,
                    )}
                    subHeadingText={t(card.subText)}
                    loanDetails={loanDetails}
                    fullView={gridEle?.fullView ?? false}
                    ratesAsOfDate={ratesAsOfDate}
                    data={leverageAndPricingData?.[gridEle.id]}
                    gridSpace={card?.gridSpace ?? 4}
                    headerClass={gridEle?.headerClass ?? ""}
                    textMaxWidth={card?.textMaxWidth}
                  />
                </Grid>
              ))}
          </Grid>
        );
    }
  }

  function renderRightPanel() {
    // sort and use
    const [tempLPConfigLeft, tempLPConfigRight, tempLpConfigFull] = [
      1, 2, 3,
    ].map((order) =>
      leverageTestsConfig?.filter((ele: ObjectType) => ele?.order === order),
    );
    return (
      <>
        {expandLeverageAndTests ? (
          <Grid container>
            <Grid item xs={expandLeverageAndTests ? 6 : 12} px={2}>
              {tempLPConfigLeft?.map((ele: ObjectType) => {
                return renderGridLayout(ele);
              })}
            </Grid>
            <Grid item xs={expandLeverageAndTests ? 6 : 12} px={2}>
              {tempLPConfigRight?.map((ele: ObjectType) => {
                return renderGridLayout(ele);
              })}
            </Grid>
            <Grid item xs={12} px={2}>
              {tempLpConfigFull?.map((ele: ObjectType) => {
                return renderGridLayout(ele);
              })}
            </Grid>
          </Grid>
        ) : (
          <>
            {leverageTestsConfig?.map((ele: ObjectType, idx: number) => {
              switch (ele?.type) {
                case "table":
                  return (
                    <Grid container key={`grid-${ele.label}-${idx}`}>
                      <Grid
                        item
                        xs={expandLeverageAndTests ? 6 : 12}
                        m={2}
                        style={{ margin: "12px" }}
                      >
                        <CustomCard
                          headingText={t(ele.label)}
                          headingActions={getGridHeadingActions(ele.label)}
                          subHeadingText={""}
                          loanDetails={loanDetails}
                          fullView={ele?.fullView ?? false}
                          ratesAsOfDate={ratesAsOfDate}
                          subComponents={
                            <LeverageTable
                              tableHeader={ele?.tableHeader ?? []}
                              tableBody={
                                leverageAndPricingData?.[ele?.label]?.tableBody
                              }
                              label={ele.label}
                              tableClassName={ele?.tableClassName ?? ""}
                              editable={isLeverageTableEditable(ele.label)}
                              overrides={pricingAdjustmentOverrides}
                              leverageOverrides={leverageAdjustedMaxOverrides}
                              isEditingCard={getIsEditingLeverageCard(
                                ele.label,
                              )}
                              draftValues={getLeverageDraftValues(ele.label)}
                              onDraftChange={getLeverageOnDraftChange(
                                ele.label,
                              )}
                            />
                          }
                          gridSpace={4}
                          data={leverageAndPricingData?.[ele.id]}
                          headerClass={ele?.headerClass ?? ""}
                        />
                      </Grid>
                    </Grid>
                  );
                case "disclaimer":
                  return renderRightPanelDisclaimerCard(ele, idx);
                default:
                  return (
                    <Grid
                      container
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                      key={`grid-${ele.label}-${idx}`}
                    >
                      {ele?.cards
                        ?.filter(
                          (card: ObjectType) =>
                            !card?.hide?.(leverageAndPricingData),
                        )
                        ?.map((card: ObjectType, lIdx: number) => {
                          return (
                            <Grid
                              item
                              style={{
                                flexGrow: "1",
                                maxWidth: card?.width || "55%",
                                margin: "4px",
                              }}
                              key={lIdx}
                              xs={expandLeverageAndTests ? 6 : 12}
                            >
                              <CustomCard
                                headingText={getHeadingText(
                                  leverageAndPricingData?.[ele.id],
                                  card,
                                )}
                                subHeadingText={t(card.subText)}
                                loanDetails={loanDetails}
                                fullView={ele?.fullView ?? false}
                                ratesAsOfDate={ratesAsOfDate}
                                data={leverageAndPricingData?.[ele.id]}
                                gridSpace={card?.gridSpace ?? 4}
                                headerClass={ele?.headerClass ?? ""}
                                showFailed={false}
                                textMaxWidth={card?.textMaxWidth}
                              />
                            </Grid>
                          );
                        })}
                    </Grid>
                  );
              }
            })}
          </>
        )}
      </>
    );
  }

  function handleTabChange(event: React.SyntheticEvent, newValue: number) {
    setCurrentTab(newValue);
    setExpandAvailableProgramsView(false);
  }

  async function submitLoanViaCreatePath(
    createPayload: any,
    loanTypeId: number,
    currentRateData: any,
  ) {
    const createResult: any = await postCreateLoan(createPayload, realmHeaders);
    if (createResult) {
      const updatePayload = normalizeSubmitApplicationLoanData(
        {
          borrowerInformation: detailedLoanSizingData?.borrowerInformation,
          loanInformation: {
            ...detailedLoanSizingData?.loanInformation,
            originationDate: detailedLoanSizingData?.loanInformation
              ?.expectedClosingDate
              ? getFormattedDate(
                  detailedLoanSizingData?.loanInformation?.expectedClosingDate,
                )
              : null,
          },
          loanEconomics: {
            ...detailedLoanSizingData?.loanEconomics,
            interestRate: parseFloat(
              currentSelectedInterestRate?.interestRate?.replace("%", "") ??
                detailedLoanSizingData?.loanEconomics?.interestRate?.replace(
                  "%",
                  "",
                ),
            ),
            originationPoints:
              leverageAndPricingData?.leverageCards?.originationPoints,
          },
          currentSelectedInterestRate: currentRateData,
          defaultOriginatorFee:
            originatorAccountMappingData?.termSheetDynamicFields,
        },
        createResult?.data?.loanId,
        loanTypeId,
        condoEligibilityFormData,
        autoCalculatedLoanResults,
        fetchData,
        loanDetails,
        latestTermSheetVersion,
      );
      const propertyPayload = normalizeSubmitApplicationPropertyData(
        detailedLoanSizingData?.propertyDetails,
        loanDetails,
        createResult?.data?.loanId,
        { loanTypeId },
        autoCalculatedPropResults,
        dynamicConfig,
      );

      // const updatePropertyURL = getURL(
      //   `public-api/aggregate/loan_property/${createResult?.data?.loanId}/properties?stage=FES`
      // );

      // need to ask
      await postUpdateLoanUrl(updatePayload, realmHeaders);
      await postUpdatePropertyUrl(
        createResult?.data?.loanId,
        propertyPayload,
        realmHeaders,
      );

      setTcLoanId(createResult?.data?.loanId);
      mapWaiverLoanIdToDestination(
        params.loanId,
        createResult?.data?.loanId,
        dynamicConfig.ViteAppKcRealm,
        dynamicConfig?.loanSubmissionFlow?.[loanType ?? ""],
      );
      setShowSnackBar(true);
      setSnackBarMsg("loanSaved");
      if (loanDetails?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING) {
        await autoGenerateTermSheet();
      }
      await handleSaveApplication(
        Boolean(createResult?.data?.loanId),
        createResult?.data?.loanId,
      );
    } else {
      setShowSnackBar(true);
      setSnackBarMsg("loanSavedFailed");
    }
  }

  async function submitLoanViaLosPath(currentRateData: any) {
    if (loanDetails?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING) {
      await getTermSheetDataBasedOnAction("autogenerate");
    }

    const body = [loanId];
    const { data } = await loanDataBasedOnLoanId(body);
    const termSheetData: any = [];
    const realmHeaders = {
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };
    await Promise.all(
      data?.data?.[0]?.termSheetVersions?.map(async (version: any) => {
        const termsheetData = await getTermSheetBasedOnVersion(
          realmHeaders,
          version.loanId,
          version?.termSheetVersion,
        );
        termSheetData.push(termsheetData?.data?.data);
      }),
    );

    const payload = {
      loanData: {
        ...data?.data?.[0],
        data: {
          ...data?.data?.[0]?.data,
          ...detailedLoanSizingData,
          documents:
            data?.data?.[0]?.data?.documents?.filter(
              (doc: any) => !doc?.isDeleted,
            ) ?? [],
          borrowerInformation: {
            ...data?.data?.[0]?.data?.borrowerInformation,
            ...detailedLoanSizingData.borrowerInformation,
            fullName: `${data?.data?.[0]?.data?.borrowerInformation?.borrowingEntityName ?? detailedLoanSizingData?.borrowerInformation?.borrowingEntityName ?? ""}`,
            borrowerEmail:
              data?.data?.[0]?.data?.borrowerInformation?.borrowerEmail ??
              detailedLoanSizingData?.borrowerInformation?.borrowerEmail ??
              "",
            firstName:
              data?.data?.[0]?.data?.borrowerInformation?.firstName ??
              detailedLoanSizingData?.borrowerInformation?.firstName ??
              "",
            lastName:
              data?.data?.[0]?.data?.borrowerInformation?.lastName ??
              detailedLoanSizingData?.borrowerInformation?.lastName ??
              "",
          },
          loanEconomics: {
            ...detailedLoanSizingData?.loanEconomics,
            interestRate: currentSelectedInterestRate?.interestRate
              ? parseFloat(
                  currentSelectedInterestRate?.interestRate?.replace("%", ""),
                )
              : detailedLoanSizingData?.loanEconomics?.interestRate,
            originationPoints:
              leverageAndPricingData?.leverageCards?.originationPoints,
          },
          currentSelectedInterestRate: currentRateData,
          defaultOriginatorFee:
            originatorAccountMappingData?.termSheetDynamicFields,
          autoCalculatedLoanResults: { ...autoCalculatedLoanResults },
          autoCalculatedPropResults: [...autoCalculatedPropResults],
        },
        termSheetData,
      },
      tenant: "sizer_to_los",
    };
    delete payload?.loanData?.termSheetVersions;
    const response = await createLoanInLos(payload, realmHeaders);

    if (response?.data?.data) {
      setTcLoanId(response?.data?.data?.loanId);
      // tc-bff-service no longer does this server-side (the mapping call
      // was removed from sizerLoanAdapter.postLoanCreationActions) — the
      // frontend is the only place this happens now, for every submission
      // path, matching submitLoanViaCreatePath below.
      mapWaiverLoanIdToDestination(
        params.loanId,
        response?.data?.data?.loanId,
        dynamicConfig.ViteAppKcRealm,
        dynamicConfig?.loanSubmissionFlow?.[loanType ?? ""],
      );
      setShowSnackBar(true);
      setSnackBarMsg("loanSaved");

      await handleSaveApplication(
        Boolean(response?.data?.data?.loanId),
        response?.data?.data?.loanId,
      );
    } else {
      setShowSnackBar(true);
      setSnackBarMsg("loanSavedFailed");
    }
  }

  async function handleSubmitApplication() {
    setIsLoading(true);
    try {
      // public api changes
      // const updateLoanURL = getURL("public-api/aggregate/loans");

      const loanTypeId = loanType === "InvestorDSCR" ? 1 : 2;
      const orgId = getCookie("orgId") || dynamicConfig?.clientId;

      const partyId = getCookie("partyId") || dynamicConfig?.partyId;

      const createPayload = {
        loanTypeId,
        originatorPartyId: orgId || "",
        loanType: loanType === "InvestorDSCR" ? "30 Year Loan" : "Bridge Loan",
        stage: "FES",
        loanOrigin: "Online Sizer",
        fundingType: "normal",
        servicerPartyId: "",
        servicerName: "BSI",
      };

      let currentRateData: any = { ...currentSelectedInterestRate };
      const selectedBuyDownOptions = buyDownOptions?.find((item: any) => {
        const itemInterestRate = item?.interestRate
          ? parseFloat(item.interestRate) * 100
          : null;
        const selectedInterestRate = currentSelectedInterestRate?.interestRate
          ? parseFloat(
              currentSelectedInterestRate?.interestRate.replace("%", ""),
            )
          : null;

        if (itemInterestRate === null || selectedInterestRate === null) {
          return false;
        }
        return itemInterestRate.toFixed(4) === selectedInterestRate.toFixed(4);
      });
      const waiverRequest = await fetchPricingExceptionsForLoan(
        params.loanId,
        buyDownOptions,
      );
      currentRateData = await calculateCurrentSelectedInterestRate(
        {
          ...currentSelectedInterestRate,
          discountPoints:
            selectedBuyDownOptions?.discountPointPercentage != null
              ? `${selectedBuyDownOptions?.discountPointPercentage?.toFixed(
                  4,
                )} %`
              : null,
          discountPointsWithDollar:
            selectedBuyDownOptions?.discountPointAmount != null
              ? formatCurrency(selectedBuyDownOptions?.discountPointAmount)
              : null,
        },
        waiverRequest,
        buyDownOptions,
      );

      if (
        !dynamicConfig.useNewDSCRFlow ||
        dynamicConfig?.loanSubmissionFlow?.[loanType ?? ""] ===
          submissionFlow.SUBMIT_TO_TC
      ) {
        await submitLoanViaCreatePath(
          createPayload,
          loanTypeId,
          currentRateData,
        );
      } else {
        await submitLoanViaLosPath(currentRateData);
      }
    } catch (err) {
      console.error(err);
      setShowSnackBar(true);
      setSnackBarMsg("loanSavedFailed");
    } finally {
      setIsLoading(false);
      setOpenWarningBeforeSubmit(false);
    }
  }

  function renderSubmitApplicationModal() {
    return (
      <Grid sx={{ padding: "24px" }}>
        {noAssignedPartner === "eligibilityAndPricingText" || isError ? (
          <div>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "500",
                lineHeight: "30px",
              }}
            >
              {t("unableToSubmit")}
            </p>
            <p
              style={{
                color: "#3C3C3C",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                textAlign: "left",
              }}
            >
              {isPricingStale
                ? t("submitPricingStaleErrorMessage")
                : isLeveragePricingOverrideStale
                  ? t("submitLeveragePricingOverrideStaleErrorMessage")
                  : t("submitValidationErrorMessage")}
            </p>
            <LoadingButton
              size="large"
              variant="contained"
              loading={isLoading}
              onClick={() => setOpenWarningBeforeSubmit(false)}
              style={{ marginTop: "16px" }}
            >
              Ok
            </LoadingButton>
          </div>
        ) : (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "8px",
              }}
            >
              <img src={WarnigIconYellow} />
              <span
                style={{ fontWeight: 600, fontSize: "18px", margin: "0px 8px" }}
              >
                {t("wait")}
              </span>
              <img src={WarnigIconYellow} />
            </div>
            <div>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "22px",
                  textAlign: "center",
                  color: "#3C3C3C",
                }}
              >
                {t("submitConfirmationBody1")}
              </span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  lineHeight: "22px",
                  textAlign: "center",
                  color: "#3C3C3C",
                }}
              >
                {t("submitConfirmationBody2")}
              </span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "22px",
                  textAlign: "center",
                  color: "#3C3C3C",
                }}
              >
                {t("submitConfirmationBody3")}
              </span>
            </div>
            {showWarningForWaiversSubmit ? (
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "22px",
                  textAlign: "center",
                  color: "#3C3C3C",
                  marginTop: "12px",
                  background: "#fffaed",
                  padding: "8px 4px",
                }}
              >
                <span>
                  <b>{t("note")}: </b>
                </span>
                <span>{t("activeRequestedWaiverConfirmation")}</span>
              </div>
            ) : null}
            {loanType === "InvestorDSCR" && assignedOrTakeOutPartnerId ? (
              <div
                style={{
                  backgroundColor: "#F8F9FD",
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  margin: "24px 0px 32px 0px",
                }}
              >
                <span
                  style={{
                    color: "#40128B",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "24px",
                    letterSpacing: "0.5px",
                    textAlign: "left",
                  }}
                >
                  Program Selected:
                </span>
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    lineHeight: "24px",
                    letterSpacing: "0.5px",
                    textAlign: "left",
                    color: "#1C1C1C",
                  }}
                >
                  {DSCROrgPartnerMapping[assignedOrTakeOutPartnerId]}
                </span>
              </div>
            ) : null}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "16px 0px",
              }}
            >
              <LoadingButton
                fullWidth
                size="large"
                variant="contained"
                loading={isLoading}
                onClick={() => handleSubmitApplication()}
              >
                Yes, Submit
              </LoadingButton>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LoadingButton
                fullWidth
                size="large"
                variant="outlined"
                loading={isLoading}
                onClick={() => setOpenWarningBeforeSubmit(false)}
              >
                Go Back
              </LoadingButton>
            </div>
          </div>
        )}
      </Grid>
    );
  }

  const isRiskPricing = useMemo(() => {
    if (leverageAndPricingData?.leverageCards?.pricingType === "RISK") {
      return "riskBasedPricing";
    }
    if (leverageAndPricingData?.leverageCards?.pricingType === "EXPERIENCE") {
      return "experienceBasedPricing";
    }
    return "";
  }, [leverageAndPricingData]);

  const expandTabs = () => {
    setExpandLeverageAndTests(!expandLeverageAndTests);
  };

  function resolvePartnerAssignment(
    data: any,
    partnerId: any,
    assignedPartner: any,
    loanData: any,
    publicAPI2Headers: any,
    isDSCR: boolean,
    loanTypeStr: string,
    newLoanDetails?: any,
  ) {
    let result: ObjectType = data;
    let tempResultKeys: ObjectType = {};
    let tempLoanResult: ObjectType = {};
    let tempPropertyResult: ObjectType = { ...autoCalculatedPropResults };
    const tempCriticalTests: ObjectType = {};

    Object.keys(data?.attributes).forEach((key) => {
      tempCriticalTests[key] =
        data?.attributes?.[key]?.criticalTestLoanEligibility;
    });

    if (partnerId) {
      result = data?.tpResultMap?.[partnerId];
      tempResultKeys = Object.keys(result?.loanResult);
      tempLoanResult = result?.loanResult[tempResultKeys[0]];
      tempPropertyResult = result?.propertiesResults;
      setAssignedPartner(partnerId ?? "");
      setLoanDetails({ ...newLoanDetails, takeOutPartner: partnerId });
      getTestTabResultData(
        data?.tpResultMap,
        loanData,
        publicAPI2Headers,
        isDSCR,
        tempLoanResult,
        loanTypeStr,
        fetchData,
        loanContextData,
        RootContextData,
        templateResponseData,
        setTemplateResponseData,
        dynamicConfig,
        templateLabels,
        assignedPartner,
        tempCriticalTests,
      );
    } else {
      setAssignedPartner("");
      // No partner assigned yet — fall back to the first available partner's
      // response so the Leverage/Pricing section (interest rate, final price,
      // etc.) still has something to show, rather than passing the still-empty
      // tempLoanResult placeholder below. Must be resolved BEFORE
      // getTestTabResultData, since that's what actually builds
      // leverageAndPricingData from tempLoanResult.
      const currentKey = Object.keys(data?.tpResultMap)[0];
      result = data?.tpResultMap?.[currentKey];
      tempResultKeys = Object.keys(result?.loanResult ?? {});
      tempLoanResult = result?.loanResult?.[tempResultKeys?.[0]];
      tempPropertyResult = result?.propertiesResults;
      getTestTabResultData(
        data?.tpResultMap,
        loanData,
        publicAPI2Headers,
        isDSCR,
        tempLoanResult,
        loanTypeStr,
        fetchData,
        loanContextData,
        RootContextData,
        templateResponseData,
        setTemplateResponseData,
        dynamicConfig,
        templateLabels,
        assignedPartner,
        tempCriticalTests,
      );
    }

    return { result, tempLoanResult, tempPropertyResult };
  }

  // Applies a successful fetchGuidelines response to state: resolves the
  // partner assignment, computes the autocalculated results, and updates
  // rules/rates/partners state. Extracted from fetchGuidelines to reduce its
  // cognitive complexity.
  function applyFetchGuidelinesResult(
    data: any,
    newLoanDetails: any,
    loanData: any,
    publicAPI2Headers: any,
    isDSCR: boolean,
    loanTypeStr: string,
  ) {
    const { setRatesAsOfDate, setCriticalTests } = loanContextData;
    const assignedPartner = data?.assignedPartner || "";
    const partnerId =
      loanDetails?.takeoutPartnerStatus === "OVERRIDDEN"
        ? loanDetails?.takeOutPartner
        : assignedPartner || "";

    const { result, tempLoanResult, tempPropertyResult } =
      resolvePartnerAssignment(
        data,
        partnerId,
        assignedPartner,
        loanData,
        publicAPI2Headers,
        isDSCR,
        loanTypeStr,
        newLoanDetails,
      );
    // Used to populate the autocalculated fields
    const finalLoanData: any = normalizeResultLoanData(
      {
        ...loanDetails,
        ...quickLoanSizingData,
        ...detailedLoanSizingData,
      },
      tempLoanResult,
      isDSCR ? "30Year" : "Bridge",
      tempPropertyResult,
      result?.propertiesResults,
      fetchData,
    );

    let asOfDate = tempLoanResult?.ruleVersion?.RATE_SHEET?.substr(1, 8) ?? "";

    if (asOfDate) {
      asOfDate = moment(asOfDate, "DD-MM-YY").format("DD-MM-YYYY");
    }

    const { tempLoanDetails, propertyResult } = finalLoanData;
    setAutoCalculatedLoanResults({ ...tempLoanDetails });
    setAutoCalculatedPropResults([...propertyResult]);
    // Merge auto-calculated loanEconomics values into detailedLoanSizingData so
    // that GetFieldUI's isAutoCalculated visibility check (currentObjValue[field.id])
    // sees the values immediately without requiring a page refresh
    if (tempLoanDetails?.loanEconomics) {
      setDetailedLoanSizingData((prev: any) => ({
        ...prev,
        loanEconomics: {
          ...prev?.loanEconomics,
          ...tempLoanDetails.loanEconomics,
        },
      }));
    }
    setRulesResponse(data);
    setRatesAsOfDate(asOfDate);
    setPartnersResults(data?.tpResultMap);
    setCriticalTests(data?.attributes);
    setBuyDownOptions(tempLoanResult?.buyDownOptions);
    setEligiblePartners(data?.eligiblePartners ?? []);
    setEligiblePartnerResults(data);
    setEligiblePartners(data.eligiblePartners);
    return { rulesData: data, asOfDate, criticalTests: data?.attributes };
  }

  const fetchGuidelines = async (
    newSizingType?: string,
    newLoanDetails?: any,
    isInitialLoad?: boolean,
  ) => {
    try {
      const publicAPI2Headers = {
        accept: "application/json",
        "kc-realm": dynamicConfig?.ViteAppKcRealm,
      };
      const loanData = {
        ...newLoanDetails,
        quickLoanSizingData,
        detailedLoanSizingData,
      };

      const isDSCR = loanType === "InvestorDSCR";
      const loanTypeStr = loanType === "InvestorDSCR" ? "30Year" : "BRIDGE";

      if (
        !Boolean(loanDetails?.tcLoanId) ||
        dynamicConfig?.allowEditAfterSubmit
      ) {
        const data = await fetchGuidelinesAndBuyDownOptions({
          loanDetails: newLoanDetails || loanDetails,
          quickLoanSizingData,
          detailedLoanSizingData,
          loanId,
          dynamicConfig,
          selectedSizingType: selectedSizingType || newSizingType,
          isInitialLoad,
          pricingAdjustmentOverrides,
          leverageAdjustedMaxOverrides,
        });
        if (Object.keys(data ?? {}).length) {
          return applyFetchGuidelinesResult(
            data,
            newLoanDetails,
            loanData,
            publicAPI2Headers,
            isDSCR,
            loanTypeStr,
          );
        }
      }
    } catch (err) {
      console.error("Something went wrong while running rules. ERROR:", err);
    }
  };

  const handleClose = (event: any, reason: string) => {
    if (reason === "clickaway") {
      return;
    }
    setShowSnackBar(false);
    setSnackBarMsg("");
  };

  const renderSnackBar = () => {
    const style =
      snackBarMsg === "loanSavedFailed" ? errorSnackBar : snackbarStyle;

    return (
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={true}
        onClose={handleClose}
        autoHideDuration={5000}
        ContentProps={{ style }}
        message={
          <div className="flex-display">
            <img
              src={
                snackBarMsg === "loanSavedFailed"
                  ? InfoErrorIcon
                  : CheckedCircle
              }
              className="mr-12"
            />
            <span>
              {t(
                snackBarMsg === "loanSavedFailed"
                  ? "loanSavedFailed"
                  : t(snackBarMsg).replace("replace", tcLoanId),
              )}
            </span>
          </div>
        }
      />
    );
  };

  // Condo Eligibility
  function preLoadCondoEligibleConfig() {
    let tempConfig = { ...condoEligibilityConfig };
    tempConfig = {
      ...tempConfig,
      fields: {
        ...tempConfig?.fields,
        ["fee"]: tempConfig?.fields?.fee?.map((feeEle: any) => {
          if (feeEle?.label === "ownerControlStartDate") {
            if (condoEligibilityFormData?.["hoaUnderOwnerControl"] === "Yes") {
              feeEle.isVisible = true;
            } else {
              feeEle.isVisible = false;
            }
          }
          if (feeEle.label === "indivdualOwnedUnits") {
            if (
              condoEligibilityFormData?.[
                "individualOwnershipAboveTwentyFive"
              ] === "Yes"
            ) {
              feeEle.isVisible = true;
            } else {
              feeEle.isVisible = false;
            }
          }
          if (feeEle.label === "hoawithLitOrBankruptcyDesc") {
            if (
              condoEligibilityFormData?.["hoawithLitOrBankruptcy"] === "Yes"
            ) {
              feeEle.isVisible = true;
            } else {
              feeEle.isVisible = false;
            }
          }
          if (feeEle.label === "restrictOwnerToRentOutDesc") {
            if (condoEligibilityFormData?.["restrictOwnerToRent"] === "Yes") {
              feeEle.isVisible = true;
            } else {
              feeEle.isVisible = false;
            }
          }
          return { ...feeEle };
        }),
      },
    };
    return tempConfig;
  }

  async function calculateEligibility() {
    try {
      const reqBody = {
        data: {
          loan: {
            loanCondominium: {
              monthlyHOAFee: condoEligibilityFormData?.monthlyHOAFee ?? null,
              hoaDuesAboveFifteen:
                condoEligibilityFormData?.hoaDuesAboveFifteen ?? null,
              hoaDuesAboveTwenty:
                condoEligibilityFormData?.hoaDuesAboveTwenty ?? null,
              reserveFundsForOneYear:
                condoEligibilityFormData?.reserveFundsForOneYear ?? null,
              reserveFundsForTwoYears:
                condoEligibilityFormData?.reserveFundsForTwoYears ?? null,
              isProjectCompleted:
                condoEligibilityFormData?.isProjectCompleted ?? null,
              subjectToAdditionalPhasing:
                condoEligibilityFormData?.subjectToAdditionalPhasing ?? null,
              soldPercentage: condoEligibilityFormData?.soldPercentage ?? null,
              hoaUnderOwnerControl:
                condoEligibilityFormData?.hoaUnderOwnerControl ?? null,
              simpleEstateOwnership:
                condoEligibilityFormData?.simpleEstateOwnership ?? null,
              ownerOccupiedUnits:
                condoEligibilityFormData?.ownerOccupiedUnits ?? null,
              renterOccupiedUnits:
                condoEligibilityFormData?.renterOccupiedUnits ?? null,
              renterOccupiedPercentage:
                condoEligibilityFormData?.renterOccupiedPercentage ?? null,
              individualOwnershipAboveTwentyFive:
                condoEligibilityFormData?.individualOwnershipAboveTwentyFive ??
                null,
              masterAssurancePolicy:
                condoEligibilityFormData?.masterAssurancePolicy ?? null,
              projectHasMobileHomes:
                condoEligibilityFormData?.projectHasMobileHomes ?? null,
              otherSourcesIncomeMoreThanTwenty:
                condoEligibilityFormData?.otherSourcesIncomeMoreThanTwenty ??
                null,
              commercialPurposesAreaMoreThanForty:
                condoEligibilityFormData?.commercialPurposesAreaMoreThanForty ??
                null,
              restrictOwnerToRent:
                condoEligibilityFormData?.restrictOwnerToRent ?? null,
              documentsWithSEC:
                condoEligibilityFormData?.documentsWithSEC ?? null,
              hoawithLitOrBankruptcy:
                condoEligibilityFormData?.hoawithLitOrBankruptcy ?? null,
              ownerControlStartDate:
                condoEligibilityFormData?.ownerControlStartDate
                  ? getFormattedDate(
                      condoEligibilityFormData?.ownerControlStartDate,
                    )
                  : null,
              indivdualOwnedUnits:
                condoEligibilityFormData?.indivdualOwnedUnits ?? null,
              hoawithLitOrBankruptcyDesc:
                condoEligibilityFormData?.hoawithLitOrBankruptcyDesc ?? null,
              restrictOwnerToRentOutDesc:
                condoEligibilityFormData?.restrictOwnerToRentOutDesc ?? null,
              condoProjectName:
                condoEligibilityFormData?.condoProjectName ?? null,
              streetAddressLine1:
                condoEligibilityFormData?.condoStreetAddress ?? null,
              noOfUnits: condoEligibilityFormData?.unit ?? null,
              state: condoEligibilityFormData?.state ?? null,
              city: condoEligibilityFormData?.city ?? null,
              postalCode: condoEligibilityFormData?.zip ?? null,
            },
          },
        },
        templateKeys: ["rule.InvestorDSCR.condominium"],
      };

      const latestVersioningDate = "12-11-2024";
      const { data } = await postCalculateEligibility(reqBody, realmHeaders);
      const condoValue = data[`rule.InvestorDSCR.condominium`];
      const isNew: any = moment(
        latestVersioningDate,
        "DD-MM-YYYY",
      ).isSameOrBefore(moment(loansApiResponse?.createdAt));
      let condoValueDisplay = "";
      switch (condoValue) {
        case "Doesn't meet TRK Min Requirements": {
          condoValueDisplay = "Not Eligible";
          break;
        }
        case "Meets TRK Standard Requirements": {
          condoValueDisplay = isNew ? "Warrantable" : "Standard";
          break;
        }
        case "Only Meets TRK Min Requirements": {
          condoValueDisplay = isNew ? "Non-Warrantable" : "Min";
          break;
        }
        default:
      }
      setLoanDetails({
        ...loanDetails,
        ["loanInformation"]: {
          ...loanDetails.loanInformation,
          condoEligibility: condoValueDisplay,
        },
        loanCondominium: reqBody?.data?.loan?.loanCondominium,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setCondoEligibility(false);
    }
  }

  function handleCondoBtnClick(button: any) {
    if (button?.type === "contained") {
      calculateEligibility();
    } else if (button?.label === "download") {
      const tempLoanDetails = {
        ...loanDetails,
        loanCondo: condoEligibilityFormData,
      };
      exportHTML({ loanDetails: tempLoanDetails, loanId, loanType });
    } else {
      setCondoEligibility(false);
    }
  }

  function CondoEligibiltyModalContent() {
    const config = preLoadCondoEligibleConfig();
    const configKeys = Object.keys(config.fields);
    return (
      <Grid container className="p-12">
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "space-between" }}
          className="mb-8"
        >
          <span>{t("toorakCondominimumQuestionnaire")}</span>
          <IconButton
            onClick={() => setCondoEligibility(false)}
            data-testid="condo-close-icon"
          >
            <img src={CloseIcon} />
          </IconButton>
        </Grid>
        {configKeys.map((key: string, idx: number) => {
          return (
            <Grid container key={idx} className="mb-8">
              {condoEligibilityConfig?.fields?.[key]
                ?.filter((ele: any) => ele.isVisible)
                .map((config: ObjectType, i: number) => {
                  return (
                    <Grid
                      item
                      xs={config?.columnSplit}
                      key={i}
                      className="mb-8 mt-12"
                    >
                      {renderFieldUI(
                        config,
                        i,
                        condoEligibilityFormData?.[config.label],
                        handleCondoEligibilityModalChange,
                        loanType,
                        theme,
                        Boolean(loanDetails?.tcLoanId),
                        t,
                        loanContextData,
                        "",
                      )}
                    </Grid>
                  );
                })}
              <Grid item xs={12} className="mb-8 mt-12">
                <Divider />
              </Grid>
            </Grid>
          );
        })}
        <Divider />
        {condoEligibilityConfig.buttons.map((button: any, index: number) => {
          return (
            <Grid item xs={button.gridSize} key={index}>
              <Button
                variant={button.type}
                onClick={() => handleCondoBtnClick(button)}
                data-testid={button?.label}
              >
                {t(button?.label)}
              </Button>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  async function getTermSheetVersionsForLoan() {
    try {
      const realmHeaders = {
        "kc-realm": dynamicConfig?.ViteAppKcRealm,
      };
      const response = await getTermSheetVersions(realmHeaders, loanId);
      setTermSheetVersions(
        response?.data?.data?.sort((a: any, b: any) => {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        }),
      );
    } catch (err) {
      console.error(err);
    }
  }

  function updateChips() {
    const partnerValues = Object.values(DSCROrgPartnerMapping);
    let tempChipData: string[] = [
      ...new Set([
        ...chipsData,
        loanDetails?.productType,
        loanDetails?.propertyType?.includes("Mixed")
          ? t("multiFamilyMixedUseLabel")
          : loanDetails?.propertyType,
      ]),
    ];

    const primaryProperty =
      detailedLoanSizingData?.propertyDetails?.[0] ?? null;

    const loanIdKey =
      dynamicConfig?.loanSubmissionFlow?.[loanType ?? ""] ===
      submissionFlow.SUBMIT_TO_TC
        ? t("tcLoanIdKey")
        : t("losLoanIdKey");
    if (loanDetails?.tcLoanId) {
      tempChipData.push(`${loanIdKey}: ${loanDetails?.tcLoanId}`);
    }

    if (loanDetails?.loanType) {
      tempChipData.push(loanDetails?.loanType);
    }
    const activeWaiver = waiverRequestData?.filter(
      (item: any) => item?.data?.isActive,
    );

    if (activeWaiver?.[0]?.data?.waiverStatus) {
      const status = activeWaiver[0]?.data?.waiverStatus;
      tempChipData.push(`Price Exception: ${status}`);
      setPriceExceptionStatus(status);
    }

    if (loanDetails?.escalations?.length) {
      const latestEscalation =
        loanDetails.escalations[loanDetails.escalations.length - 1];

      // Remove old status if it exists
      tempChipData = tempChipData.filter(
        (str: string) => !str?.includes("Escalation Review Status"),
      );

      // Add latest status
      tempChipData.push(
        `Escalation Review Status: ${latestEscalation?.underwritingStatus}`,
      );
    }

    const mappedPartner = assignedPartner
      ? DSCROrgPartnerMapping[assignedPartner]
      : null;

    if (mappedPartner && !tempChipData.includes(mappedPartner)) {
      const existingChipIndex = tempChipData.findIndex((chip: string) =>
        partnerValues.includes(chip),
      );

      if (existingChipIndex >= 0) {
        tempChipData?.splice(existingChipIndex, 1, mappedPartner);
      } else {
        tempChipData?.push(mappedPartner);
      }
    } else if (!assignedPartner) {
      tempChipData = tempChipData.filter(
        (chip: string) => !chip?.includes("DSCR"),
      );
    }
    if (
      primaryProperty?.propertyInformation?.propertyType &&
      tempChipData?.includes(loanDetails.propertyType)
    ) {
      const fetchPropertyType = loanDetails.propertyType?.includes("SFR")
        ? "Single Family/Detached"
        : loanDetails.propertyType;
      const propertyType =
        primaryProperty?.propertyInformation?.propertyType?.includes("SFR")
          ? "Single Family/Detached"
          : primaryProperty?.propertyInformation?.propertyType;
      tempChipData = tempChipData?.filter(
        (chip: string) => chip !== fetchPropertyType,
      );
      tempChipData.push(propertyType);
    }
    if (tempChipData?.length)
      setChipsData([...new Set(tempChipData.filter((item: string) => item))]);
  }

  function updateLeverageWithBuyDownOptions(data?: any) {
    const { originationPoints } = data ?? {};
    if (
      Object.keys(currentSelectedInterestRate ?? {})?.length &&
      (buyDownOptions ?? [])?.length &&
      Object.keys(leverageAndPricingData ?? {})?.length
    ) {
      const tempCurrentRateData = buyDownOptions?.find(
        (item: any) =>
          (item?.interestRate * 100).toFixed(3) ===
          parseFloat(
            currentSelectedInterestRate?.interestRate?.replace("%", ""),
          ).toFixed(3),
      );
      const finalPriceArr = [
        ...(leverageAndPricingData?.finalPriceTable?.tableBody ?? []),
      ]?.filter((ele: any) => ele[0] !== "basePrice");

      if (tempCurrentRateData) {
        const newLevAndPricData = {
          ...(leverageAndPricingData ?? {}),
          ["leverageCards"]: {
            ...(leverageAndPricingData?.leverageCards ?? {}),
            ["interestRate"]: `${(
              tempCurrentRateData?.interestRate * 100
            )?.toFixed(3)}%`,
            ["dscr"]: tempCurrentRateData?.dscr,
            ["finalPrice"]: tempCurrentRateData?.finalToorakPrice,
            ["maxLoanAmount"]: sanitizeData(
              tempCurrentRateData?.toorakLoanAmount,
              true,
              2,
            ),
            originationPoints: originationPoints,
          },
          requiredReserves: {
            ...(leverageAndPricingData?.requiredReserves ?? {}),
            dscr: tempCurrentRateData?.dscr,
            minimumRequiredReserves:
              tempCurrentRateData?.minimumBorrowerLiquidity
                ? `${sanitizeData(
                    tempCurrentRateData?.minimumBorrowerLiquidity,
                    true,
                    2,
                  )}`
                : "",
          },
          leverages: {
            tableBody: (
              leverageAndPricingData?.leverages?.tableBody ?? []
            )?.map((body: any) => {
              if (body) {
                if (body?.id === "max") {
                  return {
                    ...body,
                    data: [
                      "Max",
                      getPercentageValue(tempCurrentRateData?.maxAsIsLTV),
                      getPercentageValue(tempCurrentRateData?.maxLTC),
                    ],
                  };
                }
                if (body?.id === "adjustedMax") {
                  return {
                    ...body,
                    data: [
                      "Adjusted Max",
                      getPercentageValue(
                        tempCurrentRateData?.toorakAdjustedAsIsLtv,
                      ),
                      getPercentageValue(
                        tempCurrentRateData?.toorakAdjustedAsIsLtc,
                      ),
                    ],
                  };
                }
                return {
                  ...body,
                  data: [
                    "Requested",
                    getPercentageValue(
                      getTableValue(
                        getValue(
                          Number(body?.data?.[1]?.replace(/[+%]/g, "").trim()),
                          null,
                        ),
                        tempCurrentRateData?.toorakAdjustedAsIsLtv * 100,
                        false,
                      ),
                      false,
                      2,
                      false,
                    ),
                    getPercentageValue(
                      getTableValue(
                        getValue(
                          Number(body?.data?.[2]?.replace(/[+%]/g, "").trim()),
                          null,
                        ),
                        tempCurrentRateData?.toorakAdjustedAsIsLtv * 100,
                        false,
                      ),
                      false,
                      2,
                      false,
                    ),
                  ],
                };
              }
            }),
          },
          finalPriceTable: {
            ...(leverageAndPricingData?.finalPriceTable ?? {}),
            tableBody: [
              ...finalPriceArr,
              [finalPricingTables?.basePrice, tempCurrentRateData?.basePrice],
            ],
          },
        };
        setLeverageAndPricingData(newLevAndPricData);
      }
    }
  }

  useEffect(() => {
    const originationPoints =
      leverageAndPricingData?.leverageCards?.originationPoints;
    updateLeverageWithBuyDownOptions({ originationPoints });
  }, [
    buyDownOptions,
    currentSelectedInterestRate,
    assignedPartner,
    leverageAndPricingData?.leverageCards?.dscr,
  ]);

  useEffect(() => {
    const prepaymentPenaltyTypeMonths =
      loanDetails?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING
        ? quickLoanSizingData?.loanEconomics?.prepaymentPenaltyTypeMonths
        : detailedLoanSizingData?.loanEconomics?.prepaymentPenaltyTypeMonths;
    if (
      originatorAccountMappingData?.termSheetDynamicFields?.length &&
      !leverageAndPricingData?.leverageCards?.originationPoints
    ) {
      const originationPoints =
        originatorAccountMappingData?.termSheetDynamicFields?.find(
          (filed: any) => filed?.field === "origination_points",
        );
      if (originationPoints) {
        setLeverageAndPricingData((previous: any) => {
          return {
            ...(previous ?? {}),
            ["leverageCards"]: {
              ...(previous?.leverageCards ?? {}),
              originationPoints: originationPoints?.defaultValue,
            },
          };
        });
      }
    }
    if (
      prepaymentPenaltyTypeMonths ||
      prepaymentPenaltyTypeMonths === 0 ||
      currentSelectedInterestRate?.interestRate
    ) {
      setLeverageAndPricingData((previous: any) => {
        return {
          ...(previous ?? {}),
          ["leverageCards"]: {
            ...(previous?.leverageCards ?? {}),
            prepaymentPenaltyTypeMonths: prepaymentPenaltyTypeMonths,
            prepayPenaltyType:
              detailedLoanSizingData?.loanEconomics?.prepayPenaltyType,
          },
        };
      });
    }
  }, [
    quickLoanSizingData,
    detailedLoanSizingData,
    originatorAccountMappingData,
    currentSelectedInterestRate,
  ]);

  useEffect(() => {
    getLoanDataBasedOnLoanId();
  }, [loanId, templateLabels, loanType]);

  // Populates waiverRequestData (the "Price Exception: <status>" chip in
  // updateChips, and priceExceptionStatus gating term-sheet generation) as
  // soon as the page loads — RequestWaiverWrapper's own fetch only runs
  // once the Request Waiver tab has actually been mounted, which left an
  // already-active exception invisible until a user opened that tab.
  // buyDownOptions isn't required for waiverStatus/isActive, so this
  // doesn't need to wait on it — a later refetch (from that tab) fills in
  // the buy-down-derived fields once buyDownOptions is available.
  //
  // Guarded by a ref (not just the [params.loanId] dep array) because the
  // result of this fetch feeds updateChips's own effect (it lists
  // waiverRequestData as a dependency), and that effect's chip write can
  // trigger further re-renders — the ref makes the actual network call
  // fire at most once per loanId no matter how many times this effect
  // itself re-runs.
  const pricingExceptionFetchedLoanIdRef = useRef<string | undefined>(
    undefined,
  );
  useEffect(() => {
    if (
      !params.loanId ||
      pricingExceptionFetchedLoanIdRef.current === params.loanId
    ) {
      return;
    }
    pricingExceptionFetchedLoanIdRef.current = params.loanId;
    fetchPricingExceptionsForLoan(params.loanId)
      .then(({ data }) => {
        const withIndex =
          data?.data?.map((item, index) => ({
            ...item,
            originalIndex: index,
          })) ?? [];
        setWaiverRequestData(withIndex);
      })
      .catch((err) => {
        console.error(
          "Something went wrong while fetching pricing exceptions. ERROR:",
          err,
        );
      });
  }, [params.loanId]);

  useEffect(() => {
    setShowBanners(dynamicConfig?.ViteAppKcRealm === "bplsizer");
    let lpConfig: any = {};
    let type: string = "Bridge";
    const productType =
      loanDetails?.productType === "Bridge Loan" ? "BRIDGE" : "DSCR";
    const tabName = currentTab;

    switch (productType) {
      case "DSCR":
        switch (tabName) {
          case 0:
            // roles (5th arg) must be passed — buildDscrTab0 gates the
            // Pricing card push on it (sizingType === "" || roles includes
            // show/update_pricing_adjustment), and a real merchants loan's
            // sizingType is never "". Omitting it here (unlike the BRIDGE
            // branch below, which does pass userPermissions) meant the
            // Pricing card's permission check always saw roles=undefined,
            // silently hiding it for every merchants DSCR loan regardless
            // of the viewer's actual permissions.
            lpConfig = getLeveragePricingTestsConfig(
              "DSCR",
              0,
              loanDetails?.sizingType,
              undefined,
              userPermissions,
            );
            break;
          case 1:
            lpConfig = getLeveragePricingTestsConfig("DSCR", 1);
            break;
        }
        break; // ← critical fix: prevents fall-through into BRIDGE

      case "BRIDGE":
        if (
          loanDetails?.propertyType === "Multifamily/MixedUse" &&
          rehabTypes.includes(propType as string)
        ) {
          type += "MultiFamily";
        } else if (propType === "GroundUp") {
          type += "GroundUp";
        } else if (propType === "Fix&Flip") {
          type += "Fix&Flip";
        } else if (propType === "Stabilized") {
          type += "Stabilized";
        } else {
          type += "Rehab";
        }
        switch (tabName) {
          case 0:
            lpConfig = getLeveragePricingTestsConfig(
              type,
              0,
              propType,
              userPermissions,
            );
            break;
          case 1:
            lpConfig = getLeveragePricingTestsConfig(
              type,
              1,
              propType,
              userPermissions,
            );
            break;
        }
        break;
    }

    if (
      dynamicConfig?.ViteAppKcRealm?.includes("merchants") &&
      loanDetails?.productType === "Bridge Loan"
    ) {
      setShowBridgePricingBanner(true);
    }

    if (lpConfig.components) {
      setLeverageTestsConfig(lpConfig.components);
    }
  }, [dynamicConfig, loanDetails?.sizingType]);

  const fetchWithDebounce = useDebounce(
    useCallback(async () => {
      if (dynamicConfig?.allowEditAfterSubmit || !loanDetails?.tcLoanId) {
        try {
          if (loanType === "InvestorDSCR") {
            await fetchGuidelines(
              loanDetails?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING
                ? SizingTypeValue.QUICK_LOAN_SIZING
                : SizingTypeValue.DETAILED_LOAN_SIZING,
              loanDetails,
              false,
            );
          } else {
            await fetchGuidelineEvalResult(
              loanId,
              loanType,
              propType,
              loanContextData,
              RootContextData,
              templateResponseData,
              setTemplateResponseData,
              dynamicConfig,
              templateLabels,
            );
          }
        } finally {
          setIsTestTabLoading(false);
        }
      } else {
        setIsTestTabLoading(false);
      }
    }, [
      dynamicConfig,
      loanDetails,
      loanType,
      loanId,
      propType,
      loanContextData,
      RootContextData,
      templateResponseData,
      templateLabels,
    ]),
    2000,
  );

  useEffect(() => {
    fetchWithDebounce();
  }, [newDebouncedValue, loanDetails?.sizingType, waiverDecisionVersion]);

  // Runs on the render right after handleSavePricingEdit flips the flag —
  // by then pricingAdjustmentOverrides/leverageAndPricingData/
  // pendingAuditEdits have committed, so this render's handleSaveApplication
  // closure has the edited values, unlike a same-tick call would.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pendingPricingApplicationSave) return;
    setPendingPricingApplicationSave(false);
    handleSaveApplication(false);
  }, [pendingPricingApplicationSave]);

  // Same "run on the render right after the flag flips" reasoning as
  // pendingPricingApplicationSave above, for the Leverages card's Save
  // button (handleSaveLeveragesEdit) — without this effect,
  // setPendingLeveragesApplicationSave(true) had no consumer, so a leverage
  // edit never actually persisted to the loan record.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pendingLeveragesApplicationSave) return;
    setPendingLeveragesApplicationSave(false);
    handleSaveApplication(false);
  }, [pendingLeveragesApplicationSave]);

  useEffect(() => {
    if (loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING) {
      setQuickLoanSizingData({
        ...quickLoanSizingData,
        loanOutputs: {
          ...quickLoanSizingData?.loanOutputs,
          dscr: leverageAndPricingData?.leverageCards?.dscr,
        },
        loanEconomics: {
          prepaymentPenaltyTypeMonths: 0,
          ...(quickLoanSizingData?.loanEconomics ?? {}),
        },
      });
    }
    if (
      originatorAccountMappingData?.termSheetDynamicFields?.length &&
      !leverageAndPricingData?.leverageCards?.originationPoints
    ) {
      const originationPoints =
        originatorAccountMappingData?.termSheetDynamicFields?.find(
          (filed: any) => filed?.field === "origination_points",
        );
      if (originationPoints) {
        updateLeverageWithBuyDownOptions({
          originationPoints: originationPoints?.defaultValue,
        });
      }
    }
  }, [
    leverageAndPricingData?.leverageCards?.dscr,
    originatorAccountMappingData,
  ]);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
    getTermSheetVersionsForLoan();
    // fetchWaiverRequestData();
    const tempCondoEligibiltyConfig: ObjectType = getCondoEligibilityFields();
    setCondoEligibilityConfig(tempCondoEligibiltyConfig);
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      const confirmationMessage = t("reloadText");
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    };
    setExpandAvailableProgramsView(false);

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (decodedTokenData) {
      setCondoEligibilityFormData({
        ...condoEligibilityFormData,
        ["borrowerName"]: decodedTokenData?.name,
      });
    }
  }, [decodedTokenData]);

  useEffect(() => {
    updateChips();
  }, [
    loanDetails?.productType,
    loanDetails?.propertyType,
    loanDetails?.tcLoanId,
    loanDetails?.loanType,
    loanDetails?.escalations?.length,
    assignedPartner,
    detailedLoanSizingData?.propertyDetails?.[0]?.propertyInformation
      ?.propertyType,
    waiverDecisionVersion,
    waiverRequestData,
  ]);

  const renderTabs = () => {
    return TabLists.filter((tab: any, idx: number) => {
      return tab.isVisible({
        loanType,
        fundingType: orgFundingType === "undefined" ? "normal" : orgFundingType,
        allowPartnerSelection: dynamicConfig?.allowPartnerSelection,
        loanDetails,
        sizingType: loanDetails?.sizingType,
      });
    }).map((tab: any, idx: number) => {
      const Icon = tab.icon;
      return (
        <Tab
          key={`tab-${idx}-${tab.label}`}
          value={idx}
          icon={
            <Tooltip
              title={t(tab.label)}
              slots={{
                transition: Fade,
              }}
            >
              <img src={Icon} alt="icon" />
            </Tooltip>
          }
          label={
            <span
              style={{
                display: currentTab === idx ? "block" : "none",
                transition: "all 0.3s",
              }}
            >
              {t(tab.label)}
            </span>
          }
          sx={{
            "& .MuiTab-wrapper": {
              flexDirection: "column",
              alignItems: "center",
              transition: "all 0.3s",
            },
            "& .MuiTab-wrapper span": {
              display: currentTab === idx ? "block" : "none",
            },
            "& .MuiTab-wrapper img": {
              marginBottom: currentTab === idx ? "8px" : "0",
            },
          }}
          iconPosition="start"
        />
      );
    });
  };

  function renderHeaderSection() {
    return (
      <Header
        showSubmitLoanBtn={false}
        showSaveApplicationBtn={true}
        showBackToListBtn={
          (isUnderWriter || isCreditCommittee || isLoanProcessor) ?? false
        }
        showLinkBtn={false}
        showDownloadBtn={false}
        showBorder={true}
        validateForm={validateForm}
        handleSaveApplication={handleSaveApplication}
        setApplicationName={setApplicationName}
        applicationName={applicationName}
        isLoading={isLoading}
        openSaveApplicationModal={openSaveApplicationModal}
        setOpenSaveApplicationModal={setOpenSaveApplicationModal}
        evaluationID={evaluationID}
        handleSubmitApplication={handlePromptSubmit}
        disableSubmitApplication={Boolean(loanDetails?.tcLoanId)}
        disableSaveApplication={
          dynamicConfig?.allowEditAfterSubmit
            ? false
            : Boolean(loanDetails?.tcLoanId)
        }
        applicationError={applicationError}
        setApplicationError={setApplicationError}
        showCreateTermSheet={showTermSheet}
        showKDMButton={showKeyDealMetrics}
        showConvertBtn={
          loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING &&
          !isUnderWriter &&
          !isCreditCommittee
        }
        showSendBtn={
          loanDetails?.sizingType === SizingTypeValue.DETAILED_LOAN_SIZING &&
          !isUnderWriter &&
          !isCreditCommittee
        }
        showMenuIcon={
          [
            SizingTypeValue.QUICK_LOAN_SIZING,
            SizingTypeValue.DETAILED_LOAN_SIZING,
          ].includes(loanDetails?.sizingType) &&
          !isUnderWriter &&
          !isCreditCommittee
        }
        handleConvertion={handleConvertion}
        isLoanProcessor={isLoanProcessor}
        isUnderwriter={isUnderWriter}
        showUploadDocumentsBtn={
          dynamicConfig.showUploadDocumentBtn && !isUnderWriter
        }
        uploadDocument={uploadDocument}
        showOnlyEscalateBtn={isUnderWriter}
      />
    );
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderEscalationBanner() {
    return latestEscalation?.escalationComment ? (
      <div className="escalationBanner" data-testid="escalation-banner">
        <div className="escalationBannerTitle">
          {escalationBannerTitle}
          <span className="escalationBannerMeta">
            &ensp;{formatEscalationTimestamp(latestEscalation?.createdOn)}
            &ensp;|&ensp;
            {t("escalatedByLabel")}: {latestEscalation?.createdBy}
          </span>
        </div>

        <div className="escalationBannerReason">
          &ldquo;{latestEscalation.escalationComment}&rdquo;
        </div>
      </div>
    ) : null;
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function getLeftPanelStyle() {
    return !expandAvailableProgramsView
      ? {
          display: "block",
          borderRight: "1px solid #9FAAA9",
          width: "60%",
        }
      : {
          display: "none",
          borderRight: "1px solid #9FAAA9",
          width: "65%",
        };
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  // paddingBottom reserves room for the "Request General Waiver" footer
  // (position: fixed, see RequestGeneralWaiverFooter) so it never overlaps
  // the last bit of this panel's own content — a fixed element doesn't
  // occupy space in normal flow, so without this the bottom-most card would
  // sit partially hidden behind it.
  function getRightPanelStyle(): React.CSSProperties {
    return !expandAvailableProgramsView
      ? {
          display: "block",
          padding: "8px",
          paddingBottom: canRequestGeneralWaiver ? "72px" : "8px",
          width: "40%",
          background: "#F4F6FA",
        }
      : {
          display: "block",
          padding: "8px",
          paddingBottom: canRequestGeneralWaiver ? "72px" : "8px",
          width: "90%",
          background: "#F4F6FA",
        };
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderTab0Panel() {
    return (
      <TabPanel value={currentTab} index={0}>
        {assignedOrTakeOutPartnerId &&
        loanType === "InvestorDSCR" &&
        dynamicConfig?.allowPartnerSelection &&
        orgFundingType !== "table_funding" &&
        loanDetails?.sizingType !== SizingTypeValue.QUICK_LOAN_SIZING ? (
          <AssignedPartner
            assignedOrTakeOutPartnerId={assignedOrTakeOutPartnerId}
            DSCROrgPartnerMapping={DSCROrgPartnerMapping}
            snackBarMsg={snackBarMsg}
            setSnackBarMsg={setSnackBarMsg}
          />
        ) : null}
        <Grid container className="mt-12">
          {failedFinalRate && !buyDownOptions?.length ? (
            <div className="p-12">
              <div className="finalPrice-err-msg p-12 div-space-between">
                <img src={InfoErrorIcon} alt="info-svg" className="mr-12" />
                <p className="fs-14">{t("finalPriceCalclationError")}</p>
              </div>
            </div>
          ) : null}
          {isRiskPricing && (
            <div className="p-12 d-flex w-100">
              <div className="risk-price-msg p-12">
                <img src={InfoMsgIcon} alt="info-svg" className="mr-12 h-20" />
                <div className="fs-14">{t(isRiskPricing)}</div>
              </div>
            </div>
          )}
          {renderRightPanel()}
        </Grid>
      </TabPanel>
    );
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderBuyDownOptionsPanel() {
    return [0, 2].includes(currentTab) &&
      Object.keys(leverageAndPricingData ?? {})?.length &&
      buyDownOptions?.length ? (
      <BuyDownOptionsWrapper handleSaveApplication={handleSaveApplication} />
    ) : null;
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderQuickSizingDisclaimerPanel() {
    return [0].includes(currentTab) &&
      loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING ? (
      <Disclaimer
        boldText={t("disclaimer")}
        children={<span>{t("mmtcDisclaimerTexts")}</span>}
        customClass="disclaimerBox"
        showClose={false}
      />
    ) : null;
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderTab1Panel() {
    return (
      <TabPanel value={currentTab} index={1}>
        {isTestTabLoading ? (
          <Box
            data-testid="test-tab-loader"
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <BridgeLoanResultTab
            tabData={testTabData}
            isExpanded={isExpanded}
            expandLeverageAndTests={expandLeverageAndTests}
            setIsExpanded={setIsExpanded}
            loanDetails={loanDetails}
            isLoanDSCR={loanType === "InvestorDSCR"}
            ratesAsOfDate={ratesAsOfDate}
          />
        )}
      </TabPanel>
    );
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderTab2Panel() {
    return (
      <TabPanel value={currentTab} index={2}>
        {currentTab === 2 && buyDownOptions?.length ? (
          <RequestWaiverWrapper handleSaveApplication={handleSaveApplication} />
        ) : null}
      </TabPanel>
    );
  }

  // The "generalWaiverRequest" tab (MerchantsTabLists index 3) had no panel
  // wired up here at all — selecting it via the tab bar or via
  // renderGeneralWaiverFooterSection's onClick rendered nothing. That also
  // meant isGeneralWaiverFormOpen could never be reset back to false (its
  // only reset path is GeneralWaiverRequestTab's onCloseForm, which never
  // mounted), permanently hiding the "Request General Waiver" footer button
  // on every tab after the first click.
  function renderTab3Panel() {
    return (
      <TabPanel value={currentTab} index={3}>
        <GeneralWaiverRequestTab
          isFormOpen={isGeneralWaiverFormOpen}
          onCloseForm={() => setIsGeneralWaiverFormOpen(false)}
        />
      </TabPanel>
    );
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderGeneralWaiverFooterSection() {
    return canRequestGeneralWaiver &&
      !isGeneralWaiverFormOpen &&
      [1, 3].includes(currentTab) ? (
      <RequestGeneralWaiverFooter
        width={expandAvailableProgramsView ? "90%" : "40%"}
        onClick={() => {
          setCurrentTab(3);
          setIsGeneralWaiverFormOpen(true);
        }}
      />
    ) : null;
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderSubmitWarningModalSection() {
    return openWarningBeforeSubmit ? (
      <CustomModal
        open={openWarningBeforeSubmit}
        handleClose={setOpenWarningBeforeSubmit}
        children={renderSubmitApplicationModal()}
        className={"saveApplication"}
      />
    ) : null;
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderSnackBarSection() {
    return showSnackBar ? renderSnackBar() : null;
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderCondoEligibilityModalSection() {
    return condoEligibility ? (
      <CustomModal
        open={condoEligibility}
        handleClose={() => setCondoEligibility(false)}
        children={CondoEligibiltyModalContent()}
        className={"condoEligibility"}
      />
    ) : null;
  }

  // Extracted from the component's return JSX to reduce the cognitive
  // complexity of LoansPageV2 itself.
  function renderUploadDialogSection() {
    return dynamicConfig.showUploadDocumentBtn ? ( // && !isUnderWriter
      <UploadDialog
        files={files}
        uploading={uploading}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onRetry={(index: number) => retryDocument(index)}
      />
    ) : null;
  }

  return Object.keys(loanDetails ?? {})?.length ? (
    <div>
      {renderHeaderSection()}
      {renderEscalationBanner()}

      <div style={{ display: "flex" }}>
        <div style={getLeftPanelStyle()}>
          <Banners
            expandAvailableProgramsView={expandAvailableProgramsView}
            expandLeverageAndTests={expandLeverageAndTests}
            showLoanDetails={showLoanDetails}
            applicationName={applicationName}
            evaluationID={evaluationID}
            chipsData={chipsData}
            noAssignedPartner={noAssignedPartner}
            disclaimer={disclaimer}
            showBanners={showBanners}
            showBridgePricingBanner={showBridgePricingBanner}
          />
          {/* Components */}
          <LoanFieldsUIWrapper
            uploadDocument={uploadDocument}
            setDocumentList={setDocumentList}
            documentList={documentList}
          />
        </div>
        <div style={getRightPanelStyle()}>
          <div className="div-space-between align-flex-start border-bottom-divider">
            <Tabs value={currentTab} onChange={handleTabChange}>
              {TabLists.map((tab: any, idx: number) => {
                if (
                  tab.isVisible({
                    loanType,
                    fundingType:
                      orgFundingType === "undefined"
                        ? "normal"
                        : orgFundingType,
                    allowPartnerSelection: dynamicConfig?.allowPartnerSelection,
                    loanDetails,
                    sizingType: loanDetails?.sizingType,
                    dynamicConfig,
                    userPermissions,
                  })
                ) {
                  const Icon = tab.icon;
                  return (
                    <Tab
                      key={idx}
                      value={idx}
                      icon={
                        <Tooltip
                          title={t(tab.label)}
                          slots={{
                            transition: Fade,
                          }}
                        >
                          <img src={Icon} alt="icon" />
                        </Tooltip>
                      }
                      label={
                        <span
                          style={{
                            display: currentTab === idx ? "block" : "none",
                            transition: "all 0.3s",
                          }}
                        >
                          {t(tab.label)}
                        </span>
                      }
                      sx={{
                        "& .MuiTab-wrapper": {
                          flexDirection: "column",
                          alignItems: "center",
                          transition: "all 0.3s",
                        },
                        "& .MuiTab-wrapper span": {
                          display: currentTab === idx ? "block" : "none",
                        },
                        "& .MuiTab-wrapper img": {
                          marginBottom: currentTab === idx ? "8px" : "0",
                        },
                      }}
                      iconPosition="start"
                    />
                  );
                }
                return null;
              })}
            </Tabs>
          </div>
          {renderTab0Panel()}
          {renderBuyDownOptionsPanel()}
          {renderQuickSizingDisclaimerPanel()}
          {renderTab1Panel()}
          {renderTab2Panel()}
          {renderTab3Panel()}
          {renderSubmitWarningModalSection()}
          {renderSnackBarSection()}
          {renderCondoEligibilityModalSection()}
          {renderUploadDialogSection()}
          {/* Sticky footer — kept as the last child of the right panel so it
              always sits at the very end of this scroll container, never
              pushed above/behind any sibling above it. */}
          {renderGeneralWaiverFooterSection()}
        </div>
      </div>
    </div>
  ) : null;
}
